import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, TemplateRef, ChangeDetectorRef, HostListener, NgZone, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { Router, NavigationStart, ActivatedRoute } from '@angular/router';
import { ModalComponent } from '../primeng';
import { RequestCreditsComponent } from '../request-credits/request-credits.component';
import { ConfirmationDialogComponent } from '../confirmation-dialog/confirmation-dialog.component';
import {
  PaginationConfig,
  TableComponent,
  TableColumn,
  withCellTemplates,
} from '../primeng/table/table.component';
import { FilterPanelComponent, FilterField } from '../filter-panel/filter-panel.component';
import {
  AuthApiService,
  ListBookingsResult,
  NotebookBookingStatus,
  NOTEBOOK_BOOKING_STATUS_LABELS,
  NOTEBOOK_LIST_STATUS_MULTSELECT_OPTIONS,
  SandboxApiService,
  normalizeNotebookBookingStatus,
  parseNotebookBookingStatusSelections,
} from '@angular-workspace/services';
import { ToasterService } from '../../../services/toaster.service';
import { GoogleAnalyticsService } from '../../../services/google-analytics.service';
import { KeycloakService } from 'keycloak-angular';
import { environment } from '../../../../environments/environment';
import { filter, take, debounceTime } from 'rxjs/operators';
import { CanComponentDeactivate } from '../../../guards/my-projects-deactivate.guard';
import { ActionDropdownTriggerDirective } from '../../directives/action-dropdown-trigger.directive';
import { ActionDropdownOverlayPosition } from '../../utils/action-dropdown-position.util';
import { ActionDropdownOverlayController } from '../../utils/action-dropdown-overlay.sync';
import { Observable, Subscription } from 'rxjs';

export interface ProjectData {
  id: string;
  notebookName: string;
  notebookUrl?: string;
  storageSize: string;
  type: string;
  pvcName: string;
  cpuRequest: string;
  cpuLimit: string;
  memoryRequest: string;
  memoryLimit: string;
  status: string;
  createdAt: string;
}

/** Card list grouping for Notebook List filters */
export type NotebookListDataBucket = 'active' | 'scheduled' | 'history';

/** Badge color key — aligns with `NotebookBookingStatus` plus legacy UI tones. */
export type NotebookStatusTone =
  | NotebookBookingStatus
  | 'opening';

export interface NotebookListCard {
  id: string;
  displayTitle: string;
  subtitle: string;
  project: ProjectData;
  notebookUrl?: string;
  /** Normalized sandbox booking status when sourced from the bookings API. */
  bookingStatus?: NotebookBookingStatus;
  bucket: NotebookListDataBucket;
  statusBadge: string;
  /** CSS modifier for `.nb-table__status--*` (matches booking status when from API). */
  statusTone: NotebookStatusTone;
  slotLabel: string;
  slotValue: string;
  timeLabel: string;
  timeValue: string;
  timeHighlight: boolean;
  createdAtDisplay: string;
  categoryDisplay?: string;
  durationDisplay?: string;
  slotCount?: number;
  slotDateRaw?: string;
  slotStartAt?: string;
  slotEndAt?: string;
  iconKind: 'gpu' | 'cpu' | 'memory';
  isMock: boolean;
}

/** Rich compute preset shown on Create Notebook; maps to Sandbox `type` (+ optional GPU instance). */
export interface NotebookComputePlanOption {
  id: string;
  /** Raw backend category key used for calendar APIs (e.g. cpu_basic). */
  apiCategory?: string;
  title: string;
  description: string;
  /** Optional one-line hardware summary (shown under description). */
  cardMeta?: string;
  /** Booking / capacity caps from categories API. */
  bookingLimits?: { label: string; value: string }[];
  apiType: 'CPU' | 'GPU';
  /** Optional GPU SKU for create payload (backend may refine later per plan id). */
  sandboxGpuInstance?: string;
  icon: 'chip' | 'layers' | 'display' | 'rocket' | 'memory';
}

export interface NotebookCalendarCell {
  date: Date | null;
  key: string | null;
  /** True when this cell belongs to the month being browsed */
  inMonth: boolean;
}

/** Persisted create-notebook wizard state (sessionStorage) for refresh recovery. */
interface NotebookCreateWizardDraft {
  currentStep: number;
  form: {
    name: string;
    computePlanId: string;
    instanceType: string;
    userCategory: string;
    scheduleDate: string;
    scheduleSlots: string[];
    policyAgreed: boolean;
  };
  scheduleCalendarAnchor?: string;
}

export interface NotebookSlot {
  key: string;
  label: string;
  startTime: string;
  endTime: string;
  availability: 'available' | 'booked' | string;
  availableSlots: number;
  bookedSlots: number;
  totalSlots: number;
  /** True when the current user already holds a booking in this slot. */
  alreadyBooked?: boolean;
}

interface SandboxCategoryResource {
  id?: string;
  name?: string;
  title?: string;
  category?: string;
  description?: string;
  memory?: string;
  cpu?: string;
  storage?: string;
  gpu?: string;
  instanceType?: string;
  gpuInstanceType?: string;
  gpuInstance?: string;
  resourceType?: string;
  resource_type?: string;
  type?: string;
  configuration?: SandboxCategoryResource[];
  configurations?: SandboxCategoryResource[];
  categories?: SandboxCategoryResource[];
  resources?: SandboxCategoryResource[];
  maxActiveBookings?: number | string;
  maxBookingsPerWeek?: number | string;
  maxConcurrentUsers?: number | string;
  max_active_bookings?: number | string;
  max_bookings_per_week?: number | string;
  max_concurrent_users?: number | string;
  displayName?: string;
  display_name?: string;
  launchUrl?: string;
  launch_url?: string;
  launchMode?: string;
  launch_mode?: string;
  isBookable?: boolean;
  is_bookable?: boolean;
}

/** Browser-only category from sandbox categories API (e.g. jupyter_lite). */
interface SandboxBrowserCategory {
  launchUrl: string;
  displayName?: string;
  description?: string;
}

@Component({
  selector: 'app-my-project',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ModalComponent,
    RequestCreditsComponent,
    ConfirmationDialogComponent,
    TableComponent,
    FilterPanelComponent,
    ActionDropdownTriggerDirective,
  ],
  templateUrl: './my-project.component.html',
  styleUrl: './my-project.component.scss',
})
export class MyProjectComponent implements OnInit, AfterViewInit, OnDestroy, CanComponentDeactivate {
  private readonly productionGpuNotebookListUrl = 'https://staging.sandbox.iudx.io/api/v1/notebook/list';

  @ViewChild(RequestCreditsComponent, { static: false }) requestCreditsComponent!: RequestCreditsComponent;

  projectsData: ProjectData[] = [];

  /** Notebook List cards (from API or UI mock per environment) */
  notebookListCards: NotebookListCard[] = [];
  /** Selected booking statuses for API filter; empty = no `status` query param. */
  notebookListSelectedStatuses: NotebookBookingStatus[] = [];

  notebookListPagination: PaginationConfig = {
    enabled: true,
    currentPage: 1,
    pageSize: 10,
    totalItems: 0,
    pageSizeOptions: [10, 25, 50],
    serverSide: true,
  };

  notebookListFilterFields: FilterField[] = this.buildDefaultNotebookListFilterFields();

  // ── app-table integration ─────────────────────────────────────────────────
  @ViewChild('nbNotebookCell') nbNotebookCellTpl!: TemplateRef<any>;
  @ViewChild('nbStatusCell')   nbStatusCellTpl!:   TemplateRef<any>;
  @ViewChild('nbDurationCell') nbDurationCellTpl!: TemplateRef<any>;
  @ViewChild('nbActionsCell')  nbActionsCellTpl!:  TemplateRef<any>;

  private readonly NB_BASE_COLUMNS: TableColumn[] = [
    { key: '_card', label: 'Notebook Name' },
    { key: 'category', label: 'Category', width: '12%' },
    { key: 'slotDate', label: 'Booking Date', width: '12%' },
    { key: 'startTime', label: 'Start - End Time', width: '16%' },
    { key: 'status', label: 'Status', width: '12%' },
    { key: 'duration', label: 'Duration', width: '18%' },
    { key: 'actions', label: 'Action', width: '100px', fixed: 'right', align: 'center' },
  ];

  notebookTableColumns: TableColumn[] = this.NB_BASE_COLUMNS;

  get notebookTableRows(): any[] {
    return this.visibleNotebookCards.map(card => ({
      _card: card,
      category: this.getCategoryPart(card),
      slotDate: this.getSlotDatePart(card.slotValue),
      startTime: this.getSlotTimeRangePart(card.slotValue),
      duration: this.getDurationPart(card),
      status: card.statusBadge,
      actions: '',
    }));
  }

  /** Called after view init so ViewChild refs are resolved. */
  private bindNotebookTableTemplates(): void {
    if (!this.nbNotebookCellTpl) return;
    this.notebookTableColumns = withCellTemplates(this.NB_BASE_COLUMNS, [
      { key: '_card',   cellTemplate: this.nbNotebookCellTpl },
      { key: 'duration', cellTemplate: this.nbDurationCellTpl },
      { key: 'status',  cellTemplate: this.nbStatusCellTpl   },
      { key: 'actions', cellTemplate: this.nbActionsCellTpl  },
    ]);
    this.cdr.markForCheck();
  }

  /**
   * Notebook templates are inside a tab (`*ngIf`), so ensure they are rebound
   * after the list tab renders.
   */
  private scheduleNotebookTableTemplateBind(): void {
    Promise.resolve().then(() => {
      this.bindNotebookTableTemplates();
      this.cdr.detectChanges();
    });
  }

  private getSlotDatePart(slotValue: string): string {
    if (!slotValue || slotValue === '—') return '—';
    const [datePart] = slotValue.split(',');
    return (datePart || '').trim() || '—';
  }

  private getSlotTimeRangePart(slotValue: string): string {
    if (!slotValue || slotValue === '—') return '—';
    const [, timeRange = ''] = slotValue.split(',');
    return (timeRange || '').trim() || '—';
  }

  private getCategoryPart(card: NotebookListCard): string {
    if (card.categoryDisplay && card.categoryDisplay.trim()) return card.categoryDisplay.trim();
    const pieces = (card.subtitle || '').split('·').map((p) => p.trim()).filter(Boolean);
    return pieces.length > 0 ? pieces[0] : '—';
  }

  private getDurationPart(card: NotebookListCard): string {
    const baseDuration = (card.durationDisplay || '').trim();
    const slotCount = card.slotCount ?? 0;
    if (baseDuration && slotCount > 1) {
      return `${baseDuration} (Extended, ${slotCount} slots booked)`;
    }
    if (baseDuration) {
      return baseDuration;
    }
    const pieces = (card.subtitle || '').split('·').map((p) => p.trim()).filter(Boolean);
    return pieces.length > 1 ? pieces[pieces.length - 1] : '—';
  }

  loading = false;
  emptyMessage = 'No Notebooks Available';
  creditsLoading = true; // Loading state for credits section
  creditsRemaining: number = 0; // Will be populated from API
  creditsExpired: boolean = false; // Will be populated from API
  creditsExpirationDate: string | null = null; // Will be populated from API
  /** True when user already has a pending credit request (from GET user/credit/request) */
  hasPendingCreditRequest = false;
  pendingCreditRequestLoading = false;
  /** Pending request's additionalInfo for prepopulating the Request Credits form */
  pendingCreditRequestData: any = null;
  /** Pending credit request ID (for withdraw API) */
  pendingCreditRequestId: string | null = null;
  showWithdrawCreditModal = false;
  withdrawCreditLoading = false;
  /** Request credits history modal (granted / rejected / pending) */
  showRequestCreditsHistoryModal = false;
  creditRequestHistory: { requestedOn: string; status: string; id?: string; originalData?: any }[] = [];
  creditRequestHistoryLoading = false;
  /** Track which history action dropdown is open (index in creditRequestHistory array) */
  openHistoryActionIndex: number | null = null;
  /** Notebook list table action dropdown state */
  openNotebookActionCardId: string | null = null;
  notebookActionOverlayPosition: ActionDropdownOverlayPosition | null = null;
  private readonly notebookActionDropdownOverlay = new ActionDropdownOverlayController();
  /** Prevents document click from closing the menu on the same click that opened it. */
  private notebookActionDropdownIgnoreDocumentClose = false;
  private isLoadingNotebooks = false; // Flag to prevent multiple simultaneous API calls
  private notebooksLoaded = false; // Flag to prevent reloading on re-initialization
  
  // Polling intervals for notebooks with "Opening" status
  private statusPollingIntervals: Map<string, any> = new Map();
  // Polling interval for bookings list refresh
  private bookingPollingInterval: ReturnType<typeof setInterval> | null = null;
  private bookingPollFailures = 0;
  private readonly POLL_BASE_MS = 5000;
  private readonly POLL_MAX_MS = 60000;
  private visibilityChangeHandler = () => this.onVisibilityChange();
  private readonly notebookCreateDraftStorageKey = 'mahaagx.notebook-create-draft';
  private notebookFormDraftSub?: Subscription;
  
  // Tab state
  activeTab: 'create' | 'list' | 'request-credits' = 'list'; // Default to 'list' (Notebook List)
  /** Set true to show Request Credits tab, form, and Credits Remaining card */
  readonly requestCreditsEnabled = false;


  // Modal state (removed - now using tabs)
  showCreateNotebookModal = false;
  
  // Confirmation modal state for notebook ready
  showNotebookReadyModal = false;
  readyNotebook: ProjectData | null = null;
  readyNotebookUrl: string | null = null;
  notebookReadyModalHeading = '';
  notebookReadyModalSubline = '';
  notebookReadyModalCta = 'Open notebook';

  /** Spotlight modals for Notebook List card actions */
  notebookListModalCard: NotebookListCard | null = null;
  showNotebookStoppingModal = false;
  showNotebookExtendModal = false;
  showNotebookExtendUnavailableModal = false;
  showNotebookCancelBookingModal = false;
  showNotebookDeleteConfirmModal = false;
  showPolicyModal = false;
  /** Persistent inline feedback for My Projects (replaces error toasts). */
  myProjectFlowAlert: { type: 'error' | 'info'; message: string } | null = null;
  notebookForm!: FormGroup;
  
  // Navigation guard modal state
  showNavigationGuardModal = false;
  pendingNavigation: any = null;
  openingNotebooks: ProjectData[] = [];
  
  // Compute role access
  hasComputeRole: boolean = false;
  /** True only when the active roles array contains Compute (not pending). Used to hide GPU warning. */
  hasActiveComputeRole: boolean = false;
  pendingRoles: string[] = [];
  /** Bound handler for userRolesUpdated event (so we can remove it in ngOnDestroy) */
  private userRolesUpdatedHandler = () => this.onUserRolesUpdated();
  
  readonly defaultComputePlanId = 'cpu-compute';

  /** Fallback plans used when categories API is unavailable. */
  readonly defaultComputePlans: NotebookComputePlanOption[] = [
    {
      id: 'cpu-compute',
      title: 'CPU Compute',
      description: 'Standard general purpose workloads for data processing.',
      cardMeta: '4 vCPU · 16 GB RAM',
      apiType: 'CPU',
      icon: 'chip',
    },
    {
      id: 'gpu-high-performance',
      title: 'GPU High Performance',
      description: 'Heavy deep learning and intensive AI training tasks.',
      cardMeta: '1× NVIDIA A100 · 12 vCPU · 64 GB RAM',
      apiType: 'GPU',
      sandboxGpuInstance: 'g4dn.xlarge',
      icon: 'layers',
    },
    {
      id: 'gpu-standard',
      title: 'Standard GPU',
      description: 'Cost-effective GPU acceleration for standard machine learning models.',
      cardMeta: '1× NVIDIA T4 · 4 vCPU · 16 GB RAM',
      apiType: 'GPU',
      sandboxGpuInstance: 'g4dn.xlarge',
      icon: 'display',
    },
    {
      id: 'gpu-high-perf-alt',
      title: 'High Performance GPU',
      description: 'Unmatched performance for deep learning training and AI inference.',
      cardMeta: '1× NVIDIA A100 · 12 vCPU · 64 GB RAM',
      apiType: 'GPU',
      sandboxGpuInstance: 'g4dn.xlarge',
      icon: 'rocket',
    },
    {
      id: 'memory-optimized',
      title: 'Memory Optimized',
      description: 'High memory-to-CPU ratio for processing large datasets efficiently.',
      cardMeta: '8 vCPU · 64 GB RAM · 200 GB storage',
      apiType: 'CPU',
      icon: 'memory',
    },
  ];
  computePlans: NotebookComputePlanOption[] = [...this.defaultComputePlans];

  readonly slotAlreadyBookedMessage = 'You have already booked this slot';

  /** First day of the month shown in the schedule calendar */
  scheduleCalendarAnchor: Date;

  /** dateKey (YYYY-MM-DD) → hasAvailability from the calendar API. Empty until a category is chosen. */
  calendarAvailability: Record<string, boolean> = {};
  calendarLoading = false;

  /** Slots returned by /slots/available for the currently selected date. */
  availableSlots: NotebookSlot[] = [];
  slotsLoading = false;

  readonly weekDayLabelsShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  /** CPU / GPU from categories API; LOCAL added when browser category has launchUrl. */
  instanceTypeOptions = [
    { label: 'CPU', value: 'CPU' },
    { label: 'GPU', value: 'GPU' },
  ];
  private readonly defaultInstanceTypeOptions = [...this.instanceTypeOptions];
  private readonly localBrowserInstanceType = 'LOCAL';
  readonly MAX_NOTEBOOK_NAME_LENGTH = 50;
  /** Maximum back-to-back sessions allowed when booking a notebook (4 hours each). */
  readonly MAX_CONTIGUOUS_SCHEDULE_SLOTS = 2;
  readonly maxContiguousScheduleSlotsMessage =
    'You can book up to 2 back-to-back sessions when creating a notebook (up to 8 hours). Select consecutive sessions only.';

  /** Populated from categories API (`resourceType: browser`, `launchUrl`). */
  browserCategory: SandboxBrowserCategory | null = null;

  /** User category options rendered as cards (populated from GPU instance types API) */
  userCategoryOptions: {
    label: string;
    value: string;
    memory: string;
    description: string;
    description1: string;
    session: string;
  }[] = [];

  /** Cached GPU instance types fetched from API when GPU is selected */
  gpuInstanceTypes: any[] = [];

  private sandboxApiService = inject(SandboxApiService);
  private authApiService = inject(AuthApiService);
  private toasterService = inject(ToasterService);
  private googleAnalytics = inject(GoogleAnalyticsService);
  private keycloak = inject(KeycloakService);
  private router = inject(Router);
  private route = inject(ActivatedRoute);

  constructor(
    private cdr: ChangeDetectorRef,
    private fb: FormBuilder,
    private ngZone: NgZone
  ) {
    const n = new Date();
    this.scheduleCalendarAnchor = new Date(n.getFullYear(), n.getMonth(), 1);
  }

  /** Id → plan (filled in ngOnInit before initNotebookForm). */
  private computePlanLookup!: Record<string, NotebookComputePlanOption>;

  async ngOnInit(): Promise<void> {
    this.buildComputePlanLookup();

    // Initialize form
    this.initNotebookForm();

    // Restore tab state from URL query params (so refresh keeps the same tab)
    const tabFromUrl = this.route.snapshot.queryParamMap.get('tab');
    if (tabFromUrl && (tabFromUrl === 'list' || tabFromUrl === 'create' || tabFromUrl === 'request-credits')) {
      this.activeTab =
        tabFromUrl === 'request-credits' && !this.requestCreditsEnabled ? 'list' : tabFromUrl;
    }
    const restoredCreateDraft =
      this.activeTab === 'create' && this.restoreNotebookCreateDraft();

    // Wait for Keycloak to be ready before making API calls
    await this.waitForKeycloakReady();

    // Check if user has Compute role
    await this.checkComputeRole();

    // Listen for role updates (e.g. after Upgrade Your Role modal submits Compute request)
    if (typeof window !== 'undefined') {
      window.addEventListener('userRolesUpdated', this.userRolesUpdatedHandler);
      document.addEventListener('visibilitychange', this.visibilityChangeHandler);
    }

    // Load credit balance first (shows skeleton while loading)
    await this.loadCreditBalance();

    // If user has active compute role, check for pending credit request (form only shown when active)
    if (this.hasActiveComputeRole) {
      await this.checkPendingCreditRequest();
    }

    if (this.activeTab === 'create') {
      await this.prepareCreateNotebookTab({ preserveDraft: restoredCreateDraft });
    }

    // Then load notebooks on page load
    if (!this.notebooksLoaded) {
      await this.loadNotebooks();
    }
  }

  /**
   * Wait for Keycloak to be initialized and have a token available
   */
  private async waitForKeycloakReady(): Promise<void> {
    try {
      const keycloakInstance = this.keycloak.getKeycloakInstance();
      
      // If Keycloak is already initialized and has a token, proceed immediately
      if (keycloakInstance?.authenticated && keycloakInstance?.token) {
        console.log('Keycloak is ready, token available');
        return;
      }
      
      // Wait for Keycloak to be initialized (max 5 seconds)
      const maxWaitTime = 5000; // 5 seconds
      const checkInterval = 100; // Check every 100ms
      const startTime = Date.now();
      
      while (Date.now() - startTime < maxWaitTime) {
        const instance = this.keycloak.getKeycloakInstance();
        if (instance?.authenticated && instance?.token) {
          console.log('Keycloak is ready after waiting');
          return;
        }
        await new Promise(resolve => setTimeout(resolve, checkInterval));
      }
      
      console.warn('Keycloak ready check timed out, proceeding anyway');
    } catch (error) {
      console.error('Error waiting for Keycloak:', error);
      // Proceed anyway - the API calls will handle 401 errors
    }
  }

  /**
   * Resolves paginator total without inflating page count on every full page.
   * When the API omits `total`, page 1 may estimate `limit + 1`; deeper pages peek
   * one record at the next offset before adding another phantom page.
   */
  private async resolveNotebookListTotalItems(
    response: ListBookingsResult,
    listParams: { limit: number; offset: number; statuses?: string[] },
    baseUrl: string,
  ): Promise<number> {
    if (response.totalIsExact) {
      return response.total;
    }

    const { limit, offset, bookings } = response;
    const loaded = bookings.length;
    const floor = offset + loaded;

    if (loaded < limit) {
      return floor;
    }

    if (offset === 0) {
      return response.total;
    }

    try {
      const peek = await this.sandboxApiService.listBookings(
        { ...listParams, limit: 1, offset: offset + limit },
        baseUrl,
      );
      if (peek.totalIsExact) {
        return peek.total;
      }
      if (peek.bookings.length === 0) {
        return floor;
      }
      return Math.max(floor + 1, peek.total);
    } catch {
      return Math.max(this.notebookListPagination.totalItems ?? 0, floor);
    }
  }

  async loadNotebooks(skipLoadingState: boolean = false): Promise<void> {
    // Prevent multiple simultaneous calls
    if (this.isLoadingNotebooks) {
      console.log('Notebooks are already being loaded, skipping...');
      return;
    }

    try {
      this.isLoadingNotebooks = true;
      if (!skipLoadingState) {
        this.loading = true;
      }

      const baseUrl = this.getSandboxBaseUrlForNotebook('CPU');
      const limit = this.notebookListPagination.pageSize;
      const offset = (this.notebookListPagination.currentPage - 1) * limit;
      const listParams: { limit: number; offset: number; statuses?: string[] } = {
        limit,
        offset,
      };
      if (this.notebookListSelectedStatuses.length > 0) {
        listParams.statuses = [...this.notebookListSelectedStatuses];
      }
      const bookingsResponse = await this.sandboxApiService.listBookings(listParams, baseUrl);
      const bookings = bookingsResponse.bookings as any[];
      const totalItems = await this.resolveNotebookListTotalItems(
        bookingsResponse,
        listParams,
        baseUrl,
      );

      this.notebookListPagination = {
        ...this.notebookListPagination,
        totalItems,
        serverSide: true,
      };

      console.log(
        'Bookings loaded:',
        bookings.length,
        'total:',
        bookingsResponse.total,
        'offset:',
        bookingsResponse.offset,
      );

      this.notebookListCards = bookings.map((b: any) => this.mapBookingToNotebookCard(b));
      this.projectsData = bookings.map((b: any, i: number) =>
        this.mapNotebookToProject(this.bookingToNotebookShape(b), i),
      );

      console.log('Mapped notebookListCards:', this.notebookListCards.length);
      this.notebooksLoaded = true;
      this.isLoadingNotebooks = false;
      
      // Update loading state and ensure change detection runs
      this.loading = false;
      
      // Mark for check and detect changes immediately
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      
      // Also trigger change detection in next microtask to ensure view updates
      // This handles cases where change detection might be skipped
      Promise.resolve().then(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        if (this.notebookTableRows.length === 0) {
          this.resetNotebookListTableScroll();
        }
      });
      
      // Start polling for notebooks with "Opening" status
      this.startPollingForOpeningNotebooks();
      this.startBookingPolling();
    } catch (error) {
      console.error('Error loading notebooks:', error);
      this.loading = false;
      this.isLoadingNotebooks = false;
      this.notebookListPagination = {
        ...this.notebookListPagination,
        totalItems: 0,
      };
      // Keep existing data or show empty state
      if (this.projectsData.length === 0) {
        this.projectsData = [];
      }
      this.rebuildNotebookListCards();
      // Ensure change detection runs even on error
      this.cdr.markForCheck();
      this.cdr.detectChanges();
      Promise.resolve().then(() => {
        this.cdr.markForCheck();
        this.cdr.detectChanges();
        if (this.notebookTableRows.length === 0) {
          this.resetNotebookListTableScroll();
        }
      });
    }
  }

  /** Keeps empty-state copy centered when the table had been scrolled horizontally. */
  private resetNotebookListTableScroll(): void {
    const wrapper = document.querySelector(
      '.notebook-list-flow .table-wrapper',
    ) as HTMLElement | null;
    if (wrapper) {
      wrapper.scrollLeft = 0;
    }
  }

  private shouldMergeStagingGpuNotebookList(): boolean {
    const env = environment as { baseSandboxPathUrlCpu?: string; baseSandboxPathUrlGpu?: string };
    const cpuBase = (env.baseSandboxPathUrlCpu ?? '').trim().replace(/\/+$/, '');
    const gpuBase = (env.baseSandboxPathUrlGpu ?? '').trim().replace(/\/+$/, '');

    return (
      (cpuBase === 'https://mahaagx.maharashtra.gov.in/sandbox-api/v1' &&
        gpuBase === 'https://staging.sandbox.iudx.io/api/v1') ||
      (cpuBase === 'https://v2.dev.sandbox.iudx.io/api/v1' &&
        gpuBase === 'https://v2.dev.sandbox.iudx.io/api/v1')
    );
  }

  private extractNotebookItems(response: any): any[] {
    let notebooksArray: any[] = [];

    if (Array.isArray(response)) {
      notebooksArray = response;
    } else if (response?.result) {
      if (Array.isArray(response.result)) {
        notebooksArray = response.result;
      } else if (response.result.results && Array.isArray(response.result.results)) {
        notebooksArray = response.result.results;
      } else if (response.result.data && Array.isArray(response.result.data)) {
        notebooksArray = response.result.data;
      }
    } else if (response?.notebooks && Array.isArray(response.notebooks)) {
      notebooksArray = response.notebooks;
    } else if (response?.data && Array.isArray(response.data)) {
      notebooksArray = response.data;
    } else if (response?.items && Array.isArray(response.items)) {
      notebooksArray = response.items;
    }

    if (notebooksArray.length === 0 && response) {
      console.warn('No notebooks array found in response. Response structure:', Object.keys(response));
      console.warn('Full response:', response);
    }

    return notebooksArray;
  }

  private mergeNotebookLists(cpuNotebooks: any[], gpuNotebooks: any[]): any[] {
    const merged = [...cpuNotebooks];
    const seen = new Set(cpuNotebooks.map((item) => this.getNotebookMergeKey(item)));

    for (const notebook of gpuNotebooks) {
      const key = this.getNotebookMergeKey(notebook);
      if (!seen.has(key)) {
        merged.push(notebook);
        seen.add(key);
      }
    }

    return merged;
  }

  private getNotebookMergeKey(notebook: any): string {
    return (
      notebook?.id ||
      notebook?.notebookId ||
      notebook?.name ||
      notebook?.notebookName ||
      notebook?.pvcName ||
      JSON.stringify(notebook)
    );
  }

  private mapNotebookToProject(notebook: any, index: number): ProjectData {
    const storageSize = notebook.storageSize || notebook.storage || '';
    const type = notebook.instanceType != null && notebook.instanceType !== '' ? 'GPU' : 'CPU';

    return {
      id: notebook.id || notebook.notebookId || notebook.name || `notebook-${index}`,
      notebookName: notebook.name || notebook.notebookName || '',
      notebookUrl: notebook.notebookUrl || notebook.url || '',
      storageSize,
      type,
      pvcName: notebook.pvcName || notebook.pvc || '',
      cpuRequest: notebook.cpuRequest || notebook.cpu || '',
      cpuLimit: notebook.cpuLimit || notebook.cpuLimit || '',
      memoryRequest: notebook.memoryRequest || notebook.memory || '',
      memoryLimit: notebook.memoryLimit || notebook.memoryLimit || '',
      status: notebook.status || 'Unknown',
      createdAt: notebook.createdAt || notebook.created_at || notebook.created || ''
    };
  }

  /** Converts a booking API item into the legacy notebook shape expected by mapNotebookToProject. */
  private bookingToNotebookShape(b: any): any {
    return {
      id: String(b.id ?? b.bookingId ?? ''),
      notebookId: String(b.id ?? ''),
      notebookName: b.notebookName ?? '',
      notebookUrl: b.notebookUrl ?? b.url ?? '',
      status: b.status ?? 'Unknown',
      instanceType: (b.resourceType ?? '').toLowerCase() === 'gpu' ? 'GPU' : '',
      createdAt: b.createdAt ?? '',
      // booking-specific extras surfaced via the shape
      _booking: b,
    };
  }

  /** Maps a booking API item directly to a NotebookListCard for rich display. */
  private mapBookingToNotebookCard(b: any): NotebookListCard {
    const bookingStatus = normalizeNotebookBookingStatus(b.status);
    const st = bookingStatus ?? (b.status ?? '').trim().toLowerCase();
    const resourceType = (b.resourceType ?? '').toLowerCase();
    const iconKind: 'gpu' | 'cpu' | 'memory' = resourceType === 'gpu' ? 'gpu' : 'cpu';

    // Bucket & badge (bucket groups UI actions; filter uses bookingStatus)
    let bucket: NotebookListDataBucket;
    let statusBadge: string;
    let statusTone: NotebookStatusTone;
    switch (bookingStatus) {
      case NotebookBookingStatus.Active:
      case NotebookBookingStatus.Ready:
        bucket = 'active';
        statusBadge = NOTEBOOK_BOOKING_STATUS_LABELS[bookingStatus];
        statusTone = bookingStatus;
        break;
      case NotebookBookingStatus.Scheduled:
        bucket = 'scheduled';
        statusBadge = NOTEBOOK_BOOKING_STATUS_LABELS[bookingStatus];
        statusTone = bookingStatus;
        break;
      case NotebookBookingStatus.ShuttingDown:
        bucket = 'active';
        statusBadge = NOTEBOOK_BOOKING_STATUS_LABELS[bookingStatus];
        statusTone = bookingStatus;
        break;
      case NotebookBookingStatus.Completed:
      case NotebookBookingStatus.Cancelled:
      case NotebookBookingStatus.Expired:
        bucket = 'history';
        statusBadge = NOTEBOOK_BOOKING_STATUS_LABELS[bookingStatus];
        statusTone = bookingStatus;
        break;
      default:
        if (st === 'active' || st === 'ready') {
          bucket = 'active';
          statusBadge = st === 'ready' ? 'Ready' : 'Active';
          statusTone = st === 'ready' ? NotebookBookingStatus.Ready : NotebookBookingStatus.Active;
        } else if (st === 'scheduled') {
          bucket = 'scheduled';
          statusBadge = 'Scheduled';
          statusTone = NotebookBookingStatus.Scheduled;
        } else {
          bucket = 'history';
          statusBadge = this.titleCase(b.status ?? 'Unknown');
          statusTone = NotebookBookingStatus.Completed;
        }
    }

    // Slot label: "May 7, 16:00 – 20:00"
    const slotValue = this.formatBookingSlotRange(b.slotDate, b.slotStart, b.slotEnd);
    const isUiActive = st === 'active' || st === 'ready';
    const timeLabel = isUiActive ? 'TIME REMAINING' : st === 'scheduled' ? 'STARTS IN' : 'ENDED';
    const timeValue = this.formatBookingTimeContext(isUiActive ? 'active' : st, b.slotStart, b.slotEnd);
    const timeHighlight = isUiActive;

    const slotCount = Array.isArray(b.slotKeys) ? b.slotKeys.length : 0;
    const categoryDisplay = this.formatCategoryDisplay(
      (typeof b.displayName === 'string' && b.displayName.trim())
        ? b.displayName
        : (b.category ?? resourceType)
    );
    const durationDisplay = typeof b.duration === 'string' && b.duration.trim()
      ? b.duration.trim()
      : (slotCount > 0 ? `${slotCount * 4} Hours` : '—');
    const subtitle = [categoryDisplay, durationDisplay].filter(Boolean).join(' · ');
    const createdAtDisplay = this.formatCreatedAtDisplay(b.createdAt ?? '');

    return {
      id: String(b.id ?? b.bookingId ?? ''),
      displayTitle: b.notebookName ?? '',
      subtitle,
      bookingStatus: bookingStatus ?? undefined,
      project: {
        id: String(b.id ?? b.bookingId ?? ''),
        notebookName: b.notebookName ?? '',
        notebookUrl: b.notebookUrl ?? b.url ?? '',
        storageSize: '',
        type: resourceType === 'gpu' ? 'GPU' : 'CPU',
        pvcName: '',
        cpuRequest: '',
        cpuLimit: '',
        memoryRequest: '',
        memoryLimit: '',
        status: b.status ?? 'Unknown',
        createdAt: b.createdAt ?? '',
      },
      bucket,
      statusBadge,
      statusTone,
      slotLabel: st === 'active' || st === 'ready' ? 'CURRENT SLOT' : 'BOOKED SLOT',
      slotValue,
      timeLabel,
      timeValue,
      timeHighlight,
      createdAtDisplay,
      categoryDisplay,
      durationDisplay,
      slotCount,
      slotDateRaw: b.slotDate ?? '',
      slotStartAt: b.slotStart ?? '',
      slotEndAt: b.slotEnd ?? '',
      iconKind,
      notebookUrl: b.notebookUrl ?? b.url ?? '',
      isMock: false,
    };
  }

  private titleCase(s: string): string {
    return s.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  }

  private formatCategoryDisplay(value: string): string {
    if (!value) return '—';
    const normalized = value.replace(/[_-]+/g, ' ').trim();
    if (!normalized) return '—';
    return normalized
      .split(' ')
      .filter(Boolean)
      .map((w) => (/^(cpu|gpu)$/i.test(w) ? w.toUpperCase() : (w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())))
      .join(' ');
  }

  /** Formats slot date + fixed slot range (no timezone shift), e.g. "07-May-2026, 16:00 – 20:00". */
  private formatBookingSlotRange(slotDate: string, slotStart: string, slotEnd: string): string {
    try {
      const dateLabel = slotDate ? this.formatDate(slotDate) : '';
      const startLabel = this.formatUtcClockTime(slotStart);
      const endLabel = this.formatUtcClockTime(slotEnd);
      if (!dateLabel && !startLabel) return '—';
      if (!startLabel) return dateLabel;
      return `${dateLabel}, ${startLabel} – ${endLabel}`;
    } catch {
      return '—';
    }
  }

  /** Slot clock in 24-hour form (UTC), matching create-notebook slot tiles (e.g. 16:00). */
  private formatUtcClockTime(isoString: string): string {
    if (!isoString) return '';
    const trimmed = isoString.trim();
    const hhMm = trimmed.match(/^(\d{1,2}):(\d{2})$/);
    if (hhMm) {
      return `${hhMm[1].padStart(2, '0')}:${hhMm[2]}`;
    }
    const d = new Date(isoString);
    if (Number.isNaN(d.getTime())) return '';
    return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
  }

  private populateExtendModalCopy(card: NotebookListCard): void {
    const currentEndDate = this.getSlotEndDate(card);
    if (!currentEndDate) {
      this.extendModalCopy = { currentEnd: '—', extendedEnd: '—' };
      return;
    }

    const extendedEndDate = new Date(currentEndDate.getTime() + (4 * 60 * 60 * 1000));
    this.extendModalCopy = {
      currentEnd: this.formatModalSlotDateTime(currentEndDate),
      extendedEnd: this.formatModalSlotDateTime(extendedEndDate),
    };
  }

  private getSlotEndDate(card: NotebookListCard): Date | null {
    const raw = card.slotEndAt || '';
    if (!raw) return null;
    const d = new Date(raw);
    return Number.isNaN(d.getTime()) ? null : d;
  }

  private formatModalSlotDateTime(date: Date): string {
    const now = new Date();
    const utcStart = (d: Date) => Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
    const dayDiff = Math.floor((utcStart(date) - utcStart(now)) / (24 * 60 * 60 * 1000));
    const time = this.formatUtcClockTime(date.toISOString());

    if (dayDiff === 0) return `Today ${time}`;
    if (dayDiff === 1) return `Tomorrow ${time}`;

    const day = String(date.getUTCDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getUTCMonth()];
    const year = date.getUTCFullYear();
    return `${day}-${month}-${year} ${time}`;
  }

  /** Returns contextual time string (time remaining / starts in / ended). */
  private formatBookingTimeContext(status: string, slotStart: string, slotEnd: string): string {
    try {
      const now = Date.now();
      if (status === 'active' && slotEnd) {
        const diff = new Date(slotEnd).getTime() - now;
        if (diff > 0) return this.formatDuration(diff);
      }
      if (status === 'scheduled' && slotStart) {
        const diff = new Date(slotStart).getTime() - now;
        if (diff > 0) return this.formatDuration(diff);
      }
    } catch { /* fallthrough */ }
    return '—';
  }

  private formatDuration(ms: number): string {
    const totalMin = Math.floor(ms / 60000);
    const h = Math.floor(totalMin / 60);
    const m = totalMin % 60;
    if (h > 0 && m > 0) return `${h}h ${m}m`;
    if (h > 0) return `${h}h`;
    return `${m}m`;
  }

  private getCardBookingStatusKey(card: NotebookListCard): string {
    if (card.bookingStatus) {
      return card.bookingStatus;
    }
    return normalizeNotebookBookingStatus(card.project.status) ?? (card.project.status ?? '').trim().toLowerCase();
  }

  /** CSS class suffix for status badge (`nb-table__status--{key}`). */
  getNotebookStatusClass(card: NotebookListCard): string {
    return card.bookingStatus ?? card.statusTone;
  }

  /** Scheduled — Cancel Booking is available. */
  showNotebookCancelBookingAction(card: NotebookListCard): boolean {
    const status = normalizeNotebookBookingStatus(this.getCardBookingStatusKey(card));
    return status === NotebookBookingStatus.Scheduled;
  }

  /** Ready — Reset Booking is available. */
  showNotebookResetBookingAction(card: NotebookListCard): boolean {
    const status = normalizeNotebookBookingStatus(this.getCardBookingStatusKey(card));
    return status === NotebookBookingStatus.Ready;
  }

  /** Active or Shutting down — Open, Extend, and Delete are available. */
  showNotebookActiveLifecycleActions(card: NotebookListCard): boolean {
    const status = normalizeNotebookBookingStatus(this.getCardBookingStatusKey(card));
    return (
      status === NotebookBookingStatus.Active ||
      status === NotebookBookingStatus.ShuttingDown
    );
  }

  /** Cancelled, completed, and expired bookings show a disabled Action control (no menu). */
  isNotebookRowActionDisabled(card: NotebookListCard): boolean {
    const status = normalizeNotebookBookingStatus(this.getCardBookingStatusKey(card));
    return (
      status === NotebookBookingStatus.Completed ||
      status === NotebookBookingStatus.Cancelled ||
      status === NotebookBookingStatus.Expired
    );
  }

  getNotebookRowActionDisabledTitle(card: NotebookListCard): string | null {
    if (!this.isNotebookRowActionDisabled(card)) return null;
    const status = normalizeNotebookBookingStatus(this.getCardBookingStatusKey(card));
    if (status === NotebookBookingStatus.Cancelled) {
      return 'Actions are not available for cancelled bookings';
    }
    if (status === NotebookBookingStatus.Completed) {
      return 'Actions are not available for completed bookings';
    }
    if (status === NotebookBookingStatus.Expired) {
      return 'Actions are not available for expired bookings';
    }
    return 'Actions are not available for this booking';
  }

  get visibleNotebookCards(): NotebookListCard[] {
    if (this.isNotebookListUiMock() && this.notebookListSelectedStatuses.length > 0) {
      const selected = new Set(this.notebookListSelectedStatuses);
      return this.notebookListCards.filter((c) => {
        const key = normalizeNotebookBookingStatus(this.getCardBookingStatusKey(c));
        return key !== null && selected.has(key);
      });
    }
    return this.notebookListCards;
  }

  private syncNotebookListFilterFieldValue(): void {
    const chips = this.notebookListSelectedStatuses.map((status) => {
      const option = NOTEBOOK_LIST_STATUS_MULTSELECT_OPTIONS.find((o) => o.value === status);
      return {
        label: option?.label ?? NOTEBOOK_BOOKING_STATUS_LABELS[status],
        value: status,
      };
    });
    this.notebookListFilterFields = this.notebookListFilterFields.map((f) =>
      f.key === 'status' ? { ...f, value: chips } : f,
    );
  }

  onNotebookListFilterChange(values: Record<string, any>): void {
    const selectedArr = (values?.['status'] ?? []) as Array<{ label?: string; value?: string }>;
    this.notebookListSelectedStatuses = parseNotebookBookingStatusSelections(selectedArr);
    this.syncNotebookListFilterFieldValue();
    this.closeNotebookActionDropdown();
    this.notebooksLoaded = false;
    this.notebookListPagination = { ...this.notebookListPagination, currentPage: 1 };
    void this.loadNotebooks();
  }

  onNotebookListClearFilters(): void {
    this.resetNotebookListFilters(true);
  }

  private buildDefaultNotebookListFilterFields(): FilterField[] {
    return [
      {
        key: 'status',
        label: 'Status',
        type: 'multiselect',
        placeholder: 'Choose Status',
        options: NOTEBOOK_LIST_STATUS_MULTSELECT_OPTIONS.map((o) => ({
          label: o.label,
          value: o.value,
        })),
        value: [],
        showClear: true,
      },
    ];
  }

  /** Clears notebook list status filters (e.g. when leaving the list tab or page). */
  private resetNotebookListFilters(reload = false): void {
    this.notebookListSelectedStatuses = [];
    this.notebookListFilterFields = this.buildDefaultNotebookListFilterFields();
    this.closeNotebookActionDropdown();
    this.notebookListPagination = { ...this.notebookListPagination, currentPage: 1 };
    if (reload) {
      this.notebooksLoaded = false;
      void this.loadNotebooks();
    }
  }

  onNotebookListPageChange(page: number): void {
    this.notebookListPagination = { ...this.notebookListPagination, currentPage: page };
    this.notebooksLoaded = false;
    void this.loadNotebooks();
  }

  onNotebookListPageSizeChange(pageSize: number): void {
    this.notebookListPagination = {
      ...this.notebookListPagination,
      pageSize,
      currentPage: 1,
    };
    this.notebooksLoaded = false;
    void this.loadNotebooks();
  }

  private isNotebookListUiMock(): boolean {
    return (environment as { notebookListUiMock?: boolean }).notebookListUiMock === true;
  }

  private rebuildNotebookListCards(): void {
    if (this.isNotebookListUiMock()) {
      this.notebookListCards = this.buildNotebookListMockCards();
      return;
    }
    this.notebookListCards = this.projectsData.map((p) => this.projectToNotebookCard(p));
  }

  private projectToNotebookCard(p: ProjectData): NotebookListCard {
    const st = (p.status || '').trim().toLowerCase();
    const baseId = p.notebookName || p.id;
    const subtitle = this.notebookSubtitleLine(p);
    const iconKind = this.notebookIconKind(p);
    const createdAtDisplay = this.formatCreatedAtDisplay(p.createdAt);

    if (st === 'running') {
      return {
        id: baseId,
        displayTitle: p.notebookName,
        subtitle,
        project: p,
        bucket: 'active',
        statusBadge: 'Active',
        statusTone: NotebookBookingStatus.Active,
        slotLabel: 'CURRENT SLOT',
        slotValue: 'Today, 00:00 – 04:00',
        timeLabel: 'TIME REMAINING',
        timeValue: '—',
        timeHighlight: false,
        createdAtDisplay,
        iconKind,
        isMock: false,
      };
    }

    if (st === 'opening') {
      return {
        id: baseId,
        displayTitle: p.notebookName,
        subtitle,
        project: p,
        bucket: 'scheduled',
        statusBadge: 'Opening',
        statusTone: 'opening',
        slotLabel: 'BOOKED SLOT',
        slotValue: 'Provisioning…',
        timeLabel: 'STARTS IN',
        timeValue: '—',
        timeHighlight: false,
        createdAtDisplay,
        iconKind,
        isMock: false,
      };
    }

    return {
      id: baseId,
      displayTitle: p.notebookName,
      subtitle,
      project: p,
      bucket: 'history',
      statusBadge: st === 'stopped' ? 'Completed' : p.status || 'Unknown',
      statusTone: NotebookBookingStatus.Completed,
      slotLabel: 'USED SLOT',
      slotValue: createdAtDisplay !== '—' ? createdAtDisplay : '—',
      timeLabel: 'DURATION',
      timeValue: '—',
      timeHighlight: false,
      createdAtDisplay,
      iconKind,
      isMock: false,
    };
  }

  private notebookSubtitleLine(p: ProjectData): string {
    const t = (p.type || '').trim().toUpperCase();
    if (t === 'GPU') {
      return `Standard GPU • ${p.storageSize || '—'}`;
    }
    return `CPU Compute • ${p.storageSize || '—'}`;
  }

  private notebookIconKind(p: ProjectData): 'gpu' | 'cpu' | 'memory' {
    const t = (p.type || '').trim().toUpperCase();
    if (t === 'GPU') {
      return 'gpu';
    }
    const memRaw = (p.memoryLimit || p.memoryRequest || '').replace(/\D/g, '');
    const mem = parseInt(memRaw, 10);
    if (!Number.isNaN(mem) && mem >= 32) {
      return 'memory';
    }
    return 'cpu';
  }

  private formatCreatedAtDisplay(dateString: string): string {
    if (!dateString) {
      return '—';
    }
    const date = new Date(dateString);
    if (Number.isNaN(date.getTime())) {
      return dateString;
    }
    const day = String(date.getDate()).padStart(2, '0');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${day}-${month}-${year} ${hours}:${minutes}`;
  }

  private buildNotebookListMockCards(): NotebookListCard[] {
    const mkProj = (partial: Partial<ProjectData>): ProjectData => ({
      id: partial.id ?? 'mock',
      notebookName: partial.notebookName ?? 'mock',
      storageSize: partial.storageSize ?? '',
      type: partial.type ?? 'CPU',
      pvcName: partial.pvcName ?? '',
      cpuRequest: partial.cpuRequest ?? '',
      cpuLimit: partial.cpuLimit ?? '',
      memoryRequest: partial.memoryRequest ?? '',
      memoryLimit: partial.memoryLimit ?? '',
      status: partial.status ?? 'Unknown',
      createdAt: partial.createdAt ?? '',
    });

    return [
      {
        id: 'mock-active-cv-1',
        displayTitle: 'Computer Vision Training Workspace',
        subtitle: 'Standard GPU • 1x NVIDIA T4 • 16 GB RAM',
        project: mkProj({
          id: 'mock-active-cv-1',
          notebookName: 'computer-vision-training',
          storageSize: '65Gi',
          type: 'GPU',
          status: 'Running',
          createdAt: '2026-04-14T09:30:00',
        }),
        bucket: 'active',
        statusBadge: 'Active',
        statusTone: NotebookBookingStatus.Active,
        slotLabel: 'CURRENT SLOT',
        slotValue: 'Today, 00:00 – 04:00',
        timeLabel: 'TIME REMAINING',
        timeValue: '2h 30m',
        timeHighlight: true,
        createdAtDisplay: 'Apr 14, 2026, 9:30 AM',
        iconKind: 'gpu',
        isMock: true,
      },
      {
        id: 'mock-active-2',
        displayTitle: 'NLP Experiment Sandbox',
        subtitle: 'Standard GPU • 1x NVIDIA T4 • 16 GB RAM',
        project: mkProj({
          id: 'mock-active-2',
          notebookName: 'nlp-experiment-sandbox',
          storageSize: '65Gi',
          type: 'GPU',
          status: 'Running',
          createdAt: '2026-04-10T14:00:00',
        }),
        bucket: 'active',
        statusBadge: 'Active',
        statusTone: NotebookBookingStatus.Active,
        slotLabel: 'CURRENT SLOT',
        slotValue: 'Today, 08:00 – 12:00',
        timeLabel: 'TIME REMAINING',
        timeValue: '45m',
        timeHighlight: true,
        createdAtDisplay: 'Apr 10, 2026, 2:00 PM',
        iconKind: 'gpu',
        isMock: true,
      },
      {
        id: 'mock-active-3',
        displayTitle: 'Data Prep Workspace',
        subtitle: 'CPU Compute • 4 vCPUs • 16 GB RAM',
        project: mkProj({
          id: 'mock-active-3',
          notebookName: 'data-prep-workspace',
          storageSize: '10Gi',
          type: 'CPU',
          status: 'Running',
          createdAt: '2026-04-12T11:15:00',
        }),
        bucket: 'active',
        statusBadge: 'Active',
        statusTone: NotebookBookingStatus.Active,
        slotLabel: 'CURRENT SLOT',
        slotValue: 'Today, 06:00 – 10:00',
        timeLabel: 'TIME REMAINING',
        timeValue: '1h 15m',
        timeHighlight: true,
        createdAtDisplay: 'Apr 12, 2026, 11:15 AM',
        iconKind: 'cpu',
        isMock: true,
      },
      {
        id: 'mock-scheduled-cv',
        displayTitle: 'Computer Vision Training Workspace',
        subtitle: 'Standard GPU • 1x NVIDIA T4 • 16 GB RAM',
        project: mkProj({
          id: 'mock-scheduled-cv',
          notebookName: 'computer-vision-scheduled',
          storageSize: '65Gi',
          type: 'GPU',
          status: 'Opening',
          createdAt: '2026-04-13T16:00:00',
        }),
        bucket: 'scheduled',
        statusBadge: 'Scheduled',
        statusTone: NotebookBookingStatus.Scheduled,
        slotLabel: 'BOOKED SLOT',
        slotValue: 'Today, 04:00 – 08:00',
        timeLabel: 'STARTS IN',
        timeValue: '18h 30m',
        timeHighlight: false,
        createdAtDisplay: 'Apr 13, 2026, 4:00 PM',
        iconKind: 'gpu',
        isMock: true,
      },
      {
        id: 'mock-history-log',
        displayTitle: 'Log Analysis Workspace',
        subtitle: 'Memory Optimized • 8 vCPUs • 64 GB RAM',
        project: mkProj({
          id: 'mock-history-log',
          notebookName: 'log-analysis-workspace',
          storageSize: '10Gi',
          type: 'CPU',
          status: 'Stopped',
          createdAt: '2023-10-11T08:00:00',
        }),
        bucket: 'history',
        statusBadge: 'Completed',
        statusTone: NotebookBookingStatus.Completed,
        slotLabel: 'USED SLOT',
        slotValue: 'Oct 11, 2023',
        timeLabel: 'DURATION',
        timeValue: '4h 00m',
        timeHighlight: false,
        createdAtDisplay: 'Oct 11, 2023, 8:00 AM',
        iconKind: 'memory',
        isMock: true,
      },
    ];
  }

  /** Display title on the Notebook List spotlight modals */
  get notebookSpotlightDisplayTitle(): string {
    const c = this.notebookListModalCard;
    if (!c) {
      return 'This notebook';
    }
    return c.displayTitle || c.project.notebookName || 'This notebook';
  }

  get notebookStoppingSubline(): string {
    return `${this.notebookSpotlightDisplayTitle} is currently being stopped.`;
  }

  get extendSessionDescription(): string {
    return `You are about to extend the active slot for ${this.notebookSpotlightDisplayTitle}. This will prolong your session until the next available slot.`;
  }

  extendModalCopy = { currentEnd: 'Today 16:00', extendedEnd: 'Today 20:00' };

  onNotebookCardOpen(card: NotebookListCard): void {
    if (!card.isMock && card.notebookUrl) {
      void this.openNotebookUrlWithTokenSession(card.project, card.notebookUrl);
      return;
    }
    void this.handleAction('open', card.project, card.isMock ? card.displayTitle : undefined);
  }

  async onNotebookCardStop(card: NotebookListCard): Promise<void> {
    this.notebookListModalCard = card;
    this.showNotebookStoppingModal = true;
    this.cdr.markForCheck();

    if (card.isMock) {
      return;
    }

    const ok = await this.stopNotebook(card.project);
    if (ok) {
      setTimeout(() => {
        this.showNotebookStoppingModal = false;
        this.notebookListModalCard = null;
        this.cdr.markForCheck();
      }, 1100);
    }
  }

  closeNotebookStoppingModal(): void {
    this.showNotebookStoppingModal = false;
    this.notebookListModalCard = null;
  }

  onNotebookCardStart(card: NotebookListCard): void {
    if (card.isMock) {
      return;
    }
    void this.handleAction('start', card.project);
  }

  onNotebookCardDelete(card: NotebookListCard): void {
    this.notebookListModalCard = card;
    this.showNotebookDeleteConfirmModal = true;
    // Ensure modal visibility updates in the same event tick.
    this.cdr.detectChanges();
  }

  /** Action-dropdown optimized delete click path. */
  onNotebookActionDelete(card: NotebookListCard): void {
    this.closeNotebookActionDropdown();
    this.onNotebookCardDelete(card);
  }

  onNotebookCardExtend(card: NotebookListCard): void {
    this.notebookListModalCard = card;
    this.populateExtendModalCopy(card);
    if (this.isNotebookExtendUnavailableForCard(card)) {
      this.showNotebookExtendUnavailableModal = true;
    } else {
      this.showNotebookExtendModal = true;
    }
    // Ensure modal visibility updates in the same event tick.
    this.cdr.detectChanges();
  }

  /**
   * Action-dropdown optimized extend click path.
   * Closes dropdown first, then opens modal immediately to avoid perceived lag.
   */
  onNotebookActionExtend(card: NotebookListCard): void {
    if (this.isNotebookExtendDisabled(card)) return;
    this.closeNotebookActionDropdown();
    this.onNotebookCardExtend(card);
  }

  /** CPU/GPU Basic sessions already at the 8-hour (2-slot) cap cannot be extended further. */
  isNotebookExtendDisabled(card: NotebookListCard): boolean {
    return this.isCpuOrGpuBasicNotebookCard(card) && this.isEightHourNotebookDuration(card);
  }

  getNotebookExtendDisabledTitle(card: NotebookListCard): string | null {
    if (!this.isNotebookExtendDisabled(card)) return null;
    return 'This session has already been extended and cannot be extended again.';
  }

  private isCpuOrGpuBasicNotebookCard(card: NotebookListCard): boolean {
    const category = this.getCategoryPart(card).replace(/\s+/g, ' ').trim().toLowerCase();
    return category === 'cpu basic' || category === 'gpu basic';
  }

  private isEightHourNotebookDuration(card: NotebookListCard): boolean {
    if ((card.slotCount ?? 0) >= 2) return true;
    const duration = (card.durationDisplay || '').trim().toLowerCase();
    return /^8\s*hours?\b/.test(duration);
  }

  /** Demo: NLP mock card triggers “extension unavailable” per design specs */
  private isNotebookExtendUnavailableForCard(card: NotebookListCard): boolean {
    return card.id === 'mock-active-2';
  }

  closeNotebookExtendModal(): void {
    this.showNotebookExtendModal = false;
    this.notebookListModalCard = null;
  }

  async confirmNotebookExtendModal(): Promise<void> {
    const card = this.notebookListModalCard;
    if (!card) {
      return;
    }

    this.showNotebookExtendModal = false;

    // Keep mock behavior for placeholder cards.
    if (card.isMock) {
      const title = card.displayTitle ?? '';
      this.notebookListModalCard = null;
      this.toasterService.success(
        title ? `Extension confirmed for ${title}.` : 'Extension confirmed.',
      );
      return;
    }

    try {
      const bookingId = String(card.id ?? card.project?.id ?? '').trim();
      if (!bookingId) {
        throw new Error('Booking id not found');
      }

      const baseUrl = this.getSandboxBaseUrlForNotebook(card.project?.type);
      await this.sandboxApiService.extendBooking(bookingId, baseUrl);

      this.toasterService.success('Notebook session extended successfully.');

      // Refresh to reflect updated slot window/status.
      this.notebooksLoaded = false;
      await this.loadNotebooks(true);
    } catch (error) {
      console.error('Error extending notebook session:', error);
      const err = error as any;
      const detail =
        err?.error?.detail ??
        err?.detail ??
        err?.error?.message ??
        err?.message ??
        '';
      this.showMyProjectFlowError(
        detail
          ? `Failed to extend notebook session: ${detail}`
          : 'Failed to extend notebook session. Please try again.',
      );
    } finally {
      this.notebookListModalCard = null;
    }
  }

  closeNotebookExtendUnavailableModal(): void {
    this.showNotebookExtendUnavailableModal = false;
    this.notebookListModalCard = null;
  }

  onNotebookCardCancelBooking(card: NotebookListCard): void {
    this.notebookListModalCard = card;
    this.showNotebookCancelBookingModal = true;
  }

  async onNotebookCardResetBooking(card: NotebookListCard): Promise<void> {
    if (card.isMock) {
      this.toasterService.success('Booking reset.');
      return;
    }

    try {
      const bookingId = this.resolveNotebookBookingId(card);
      if (!bookingId) {
        throw new Error('Booking id not found');
      }

      const baseUrl = this.getSandboxBaseUrlForNotebook(card.project?.type);
      await this.sandboxApiService.resetBooking(bookingId, baseUrl);

      this.toasterService.success('Booking reset successfully.');
      this.notebooksLoaded = false;
      await this.loadNotebooks(true);
    } catch (error) {
      console.error('Error resetting booking:', error);
      const err = error as any;
      const detail =
        err?.error?.detail ??
        err?.detail ??
        err?.error?.message ??
        err?.message ??
        '';
      this.showMyProjectFlowError(
        detail ? `Failed to reset booking: ${detail}` : 'Failed to reset booking. Please try again.',
      );
    }
  }

  /** Booking id for sandbox PATCH actions (`/v1/bookings/{id}/…`). */
  private resolveNotebookBookingId(card: NotebookListCard): string {
    const booking = (card.project as ProjectData & { _booking?: { id?: unknown; bookingId?: unknown } })?._booking;
    return String(
      card.id ??
        booking?.id ??
        booking?.bookingId ??
        card.project?.id ??
        '',
    ).trim();
  }

  closeNotebookCancelBookingModal(): void {
    this.showNotebookCancelBookingModal = false;
    this.notebookListModalCard = null;
  }

  async confirmNotebookCancelBooking(): Promise<void> {
    const card = this.notebookListModalCard;
    if (!card) {
      return;
    }
    this.showNotebookCancelBookingModal = false;

    if (card.isMock) {
      this.toasterService.success('Booking cancelled.');
      this.notebookListModalCard = null;
      return;
    }

    try {
      const bookingId = String(card.id ?? card.project?.id ?? '').trim();
      if (!bookingId) {
        throw new Error('Booking id not found');
      }

      const baseUrl = this.getSandboxBaseUrlForNotebook(card.project?.type);
      await this.sandboxApiService.cancelBooking(bookingId, baseUrl);

      this.toasterService.success('Booking cancelled successfully.');
      this.notebooksLoaded = false;
      await this.loadNotebooks(true);
    } catch (error) {
      console.error('Error cancelling booking:', error);
      const err = error as any;
      const detail =
        err?.error?.detail ??
        err?.detail ??
        err?.error?.message ??
        err?.message ??
        '';
      this.showMyProjectFlowError(
        detail ? `Failed to cancel booking: ${detail}` : 'Failed to cancel booking. Please try again.',
      );
    } finally {
      this.notebookListModalCard = null;
    }
  }

  closeNotebookDeleteConfirmModal(): void {
    this.showNotebookDeleteConfirmModal = false;
    this.notebookListModalCard = null;
  }

  async confirmNotebookDeleteModal(): Promise<void> {
    const card = this.notebookListModalCard;
    if (!card) {
      return;
    }

    if (card.isMock) {
      this.showNotebookDeleteConfirmModal = false;
      this.notebookListModalCard = null;
      this.showMyProjectFlowInfo('This is demo data — delete applies to live notebooks only.');
      return;
    }

    try {
      const bookingId = String(card.id ?? card.project?.id ?? '').trim();
      if (!bookingId) {
        throw new Error('Booking id not found');
      }

      const baseUrl = this.getSandboxBaseUrlForNotebook(card.project?.type);
      await this.sandboxApiService.terminateBooking(bookingId, baseUrl);

      this.showNotebookDeleteConfirmModal = false;
      this.notebookListModalCard = null;
      this.toasterService.success("You've successfully deleted the Notebook");

      // Refresh bookings list after successful terminate (HTTP 200 OK path).
      this.notebooksLoaded = false;
      await this.loadNotebooks(true);
    } catch (error) {
      console.error('Error terminating booking:', error);
      const err = error as any;
      const detail =
        err?.error?.detail ??
        err?.detail ??
        err?.error?.message ??
        err?.message ??
        '';
      this.showMyProjectFlowError(
        detail
          ? `Failed to delete notebook: ${detail}`
          : 'Failed to delete notebook. Please try again.',
      );
    }
  }

  initNotebookForm(): void {
    this.notebookFormDraftSub?.unsubscribe();
    const n = new Date();
    this.scheduleCalendarAnchor = new Date(n.getFullYear(), n.getMonth(), 1);
    this.notebookForm = this.fb.group({
      name: ['', [Validators.required, this.notebookNameValidator]],
      computePlanId: ['', [Validators.required]],
      instanceType: ['', [Validators.required]],
      userCategory: [''],
      scheduleDate: ['', [Validators.required]],
      scheduleSlots: [[] as string[], [this.scheduleSlotsValidatorFn]],
      policyAgreed: [false, [Validators.requiredTrue]],
    });
    this.notebookFormDraftSub = this.notebookForm.valueChanges
      .pipe(debounceTime(200))
      .subscribe(() => this.persistNotebookCreateDraft());
  }

  private persistNotebookCreateDraft(): void {
    if (this.activeTab !== 'create' || !this.notebookForm) return;
    if (typeof sessionStorage === 'undefined') return;
    try {
      const v = this.notebookForm.getRawValue();
      const draft: NotebookCreateWizardDraft = {
        currentStep: this.currentStep,
        form: {
          name: v.name ?? '',
          computePlanId: v.computePlanId ?? '',
          instanceType: v.instanceType ?? '',
          userCategory: v.userCategory ?? '',
          scheduleDate: v.scheduleDate ?? '',
          scheduleSlots: Array.isArray(v.scheduleSlots) ? v.scheduleSlots : [],
          policyAgreed: !!v.policyAgreed,
        },
        scheduleCalendarAnchor: this.scheduleCalendarAnchor.toISOString(),
      };
      sessionStorage.setItem(this.notebookCreateDraftStorageKey, JSON.stringify(draft));
    } catch {
      // Ignore quota / private-mode errors
    }
  }

  private restoreNotebookCreateDraft(): boolean {
    if (typeof sessionStorage === 'undefined') return false;
    try {
      const raw = sessionStorage.getItem(this.notebookCreateDraftStorageKey);
      if (!raw) return false;
      const draft = JSON.parse(raw) as NotebookCreateWizardDraft;
      if (!draft?.form) return false;

      this.notebookForm.patchValue(
        {
          name: draft.form.name ?? '',
          computePlanId: draft.form.computePlanId ?? '',
          instanceType: draft.form.instanceType ?? '',
          userCategory: draft.form.userCategory ?? '',
          scheduleDate: draft.form.scheduleDate ?? '',
          scheduleSlots: Array.isArray(draft.form.scheduleSlots) ? draft.form.scheduleSlots : [],
          policyAgreed: !!draft.form.policyAgreed,
        },
        { emitEvent: false },
      );

      if (draft.scheduleCalendarAnchor) {
        const anchor = new Date(draft.scheduleCalendarAnchor);
        if (!Number.isNaN(anchor.getTime())) {
          this.scheduleCalendarAnchor = new Date(anchor.getFullYear(), anchor.getMonth(), 1);
        }
      }

      const instanceType = (draft.form.instanceType ?? '').toString();
      if (instanceType) {
        this.applyNotebookFormValidatorsForInstanceType(instanceType);
      }

      this.currentStep = this.resolveRestoredCreateStep(draft.currentStep ?? 1);
      return true;
    } catch {
      return false;
    }
  }

  private resolveRestoredCreateStep(requestedStep: number): number {
    let step = Math.min(4, Math.max(1, Math.round(Number(requestedStep) || 1)));
    if (this.isLocalBrowserInstanceType() && (step === 2 || step === 3)) {
      step = 4;
    }
    if (!this.isStepLocked(step)) return step;
    for (let s = step; s >= 1; s--) {
      if (!this.isStepLocked(s)) return s;
    }
    return 1;
  }

  private hasNotebookCreateDraft(): boolean {
    if (typeof sessionStorage === 'undefined') return false;
    try {
      const raw = sessionStorage.getItem(this.notebookCreateDraftStorageKey);
      if (!raw) return false;
      const draft = JSON.parse(raw) as NotebookCreateWizardDraft;
      return !!draft?.form;
    } catch {
      return false;
    }
  }

  private clearNotebookCreateDraft(): void {
    if (typeof sessionStorage === 'undefined') return;
    try {
      sessionStorage.removeItem(this.notebookCreateDraftStorageKey);
    } catch {
      // ignore
    }
  }

  private async rehydrateNotebookScheduleAfterDraftRestore(): Promise<void> {
    const planId = this.notebookForm.get('computePlanId')?.value as string;
    if (!planId) return;
    const plan = this.computePlanLookup?.[planId];
    if (!plan) return;
    const category = plan.apiCategory ?? planId.replace(/-/g, '_');
    await this.loadCalendarSlots(category);
    const dateKey = this.notebookForm.get('scheduleDate')?.value as string;
    if (dateKey) {
      await this.loadAvailableSlots(dateKey);
    }
  }

  private readonly scheduleSlotsValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const v = control.value as unknown;
    if (!Array.isArray(v) || v.length === 0) {
      return { slotsRequired: true };
    }
    if (v.length > this.MAX_CONTIGUOUS_SCHEDULE_SLOTS) {
      return { maxContiguousSlots: true };
    }
    if (!this.areSelectedScheduleSlotsContiguous(v)) {
      return { nonContiguousSlots: true };
    }
    return null;
  };

  private areSelectedScheduleSlotsContiguous(selectedKeys: string[]): boolean {
    const indices = selectedKeys
      .map((key) => this.availableSlots.findIndex((slot) => slot.key === key))
      .filter((index) => index >= 0)
      .sort((a, b) => a - b);

    if (indices.length <= 1) {
      return true;
    }

    for (let i = 1; i < indices.length; i++) {
      if (indices[i] !== indices[i - 1] + 1) {
        return false;
      }
    }
    return true;
  }

  // Custom validator for notebook name
  notebookNameValidator = (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;
    if (!value) {
      return null; // Let required validator handle empty values
    }

    // Minimum 4 characters
    if (typeof value === 'string' && value.length < 4) {
      return { minLength: true };
    }

    if (typeof value === 'string' && value.length > this.MAX_NOTEBOOK_NAME_LENGTH) {
      return { maxLength: true };
    }

    // Only lowercase letters are accepted
    if (value !== value.toLowerCase()) {
      return { lowercaseOnly: true };
    }

    // Must start with a letter or number
    if (!/^[a-z0-9]/.test(value)) {
      return { mustStartWithLetterOrNumber: true };
    }

    // Hyphens only between characters: not at start/end, no consecutive hyphens
    // Valid: abc-def, a-b-c
    // Invalid: -abc, abc-, test---noi
    if (/^-|-$/.test(value)) {
      return { invalidHyphenEdge: true };
    }
    if (/--/.test(value)) {
      return { invalidHyphenConsecutive: true };
    }

    // Only allow lowercase letters, numbers, and hyphens
    if (!/^[a-z0-9-]+$/.test(value)) {
      return { invalidCharacters: true };
    }

    return null;
  };

  /** Show inline name errors while typing, not only after blur. */
  shouldShowNotebookNameErrors(): boolean {
    const nameControl = this.notebookForm.get('name');
    if (!nameControl?.invalid) return false;
    const value = (nameControl.value ?? '').toString();
    return nameControl.touched || nameControl.dirty || value.length > 0;
  }

  getNotebookNameErrors(): string {
    const nameControl = this.notebookForm.get('name');
    if (nameControl?.errors) {
      if (nameControl.errors['required']) {
        return 'Notebook name is required';
      }
      if (nameControl.errors['minLength']) {
        return 'Notebook name must be at least 4 characters';
      }
      if (nameControl.errors['maxLength']) {
        return `Notebook name must be at most ${this.MAX_NOTEBOOK_NAME_LENGTH} characters`;
      }
      if (nameControl.errors['lowercaseOnly']) {
        return 'Only lowercase letters are accepted';
      }
      if (nameControl.errors['mustStartWithLetterOrNumber']) {
        return 'Must start with a letter or number';
      }
      if (nameControl.errors['invalidHyphenEdge']) {
        return 'Hyphens cannot be at the start or end of the name';
      }
      if (nameControl.errors['invalidHyphenConsecutive']) {
        return 'Only one hyphen at a time (no consecutive hyphens)';
      }
      if (nameControl.errors['invalidCharacters']) {
        return 'Only lowercase letters, numbers, and hyphens are allowed';
      }
    }
    return '';
  }

  // ── Stepper wizard state for Create Notebook ─────────────────────────────────

  currentStep = 1;

  isLocalBrowserInstanceType(type?: string): boolean {
    const t = (type ?? this.notebookForm?.get('instanceType')?.value ?? '')
      .toString()
      .trim()
      .toUpperCase();
    return t === this.localBrowserInstanceType;
  }

  isBrowserLaunchAvailable(): boolean {
    return !!this.browserCategory?.launchUrl?.trim();
  }

  /** Local/browser card sublabel — API text without the WebAssembly suffix. */
  getLocalBrowserInstanceSublabel(): string {
    const fallback = 'Runs on your device CPU in the browser — no slot booking';
    const raw = this.browserCategory?.description?.trim();
    if (!raw) return fallback;
    const cleaned = raw
      .replace(/\s*powered by WebAssembly\s*/gi, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    return cleaned || fallback;
  }

  /**
   * `{sandboxApiMount}{launchPath}` — launchPath from session or categories API.
   * Strips trailing `/v1` from the CPU sandbox base so dedicated hosts
   * (`…/api/v1`) and proxied mounts (`…/sandbox-api/v1`) both resolve correctly.
   */
  buildSandboxBrowserLaunchUrl(launchPath?: string): string | null {
    const path = (launchPath ?? this.browserCategory?.launchUrl ?? '').trim();
    if (!path) return null;
    if (/^https?:\/\//i.test(path)) {
      return path;
    }

    const base = this.getSandboxBaseUrlForNotebook('CPU').trim();
    // `/api/v1` → `/api`; `/sandbox-api/v1` → `/sandbox-api`
    let apiMount: string;
    try {
      const u = new URL(base);
      const pathname = u.pathname.replace(/\/+$/, '').replace(/\/v1$/i, '');
      apiMount = `${u.origin}${pathname}`;
    } catch {
      apiMount = base.replace(/\/+$/, '').replace(/\/v1$/i, '');
    }

    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${apiMount}${normalizedPath}`;
  }

  isStepCompleted(step: number): boolean {
    switch (step) {
      case 1: return !!this.notebookForm.get('instanceType')?.value;
      case 2:
        if (this.isLocalBrowserInstanceType()) {
          return this.isStepCompleted(1);
        }
        return !!this.notebookForm.get('name')?.valid;
      case 3:
        if (this.isLocalBrowserInstanceType()) {
          return this.isStepCompleted(1);
        }
        return !!this.notebookForm.get('computePlanId')?.value;
      case 4: {
        if (this.isLocalBrowserInstanceType()) {
          return !!this.notebookForm.get('policyAgreed')?.value;
        }
        const slots = this.notebookForm.get('scheduleSlots')?.value;
        return Array.isArray(slots) && slots.length > 0;
      }
      case 5: return !!this.notebookForm.get('policyAgreed')?.value;
      default: return false;
    }
  }

  isFormReadyToSubmit(): boolean {
    if (this.isLocalBrowserInstanceType()) {
      return (
        !!this.notebookForm.get('instanceType')?.value &&
        !!this.notebookForm.get('policyAgreed')?.value &&
        this.isBrowserLaunchAvailable()
      );
    }
    const base =
      !!this.notebookForm.get('name')?.valid &&
      !!this.notebookForm.get('instanceType')?.value &&
      !!this.notebookForm.get('policyAgreed')?.value;
    return (
      base &&
      !!this.notebookForm.get('computePlanId')?.value &&
      !!this.notebookForm.get('scheduleDate')?.value &&
      Array.isArray(this.notebookForm.get('scheduleSlots')?.value) &&
      this.notebookForm.get('scheduleSlots')!.value.length > 0
    );
  }

  isStepLocked(step: number): boolean {
    if (step <= 1) return false;
    if (this.isLocalBrowserInstanceType()) {
      if (step === 2 || step === 3) return true;
      if (step === 4) return !this.isStepCompleted(1);
    }
    return !this.isStepCompleted(step - 1);
  }

  get notebookCreateSteps(): { label: string }[] {
    return [
      { label: 'Instance Type' },
      { label: 'Notebook Name' },
      { label: 'Categories' },
      {
        label: this.isLocalBrowserInstanceType()
          ? 'Agreement'
          : 'Date & Session',
      },
    ];
  }

  isNotebookStepTrackActive(index: number): boolean {
    return this.currentStep === index + 1;
  }

  isNotebookStepTrackCompleted(index: number): boolean {
    return index + 1 < this.currentStep;
  }

  isNotebookStepTrackClickable(index: number): boolean {
    return !this.isStepLocked(index + 1);
  }

  isNotebookStepTrackConnectorCompleted(index: number): boolean {
    return (
      this.isNotebookStepTrackCompleted(index + 1) ||
      this.isNotebookStepTrackActive(index + 1)
    );
  }

  goToStep(step: number): void {
    if (this.isStepLocked(step)) return;
    if (step === 1) {
      this.clearMyProjectFlowAlert();
    }
    this.currentStep = step;
    this.persistNotebookCreateDraft();
  }

  nextStep(): void {
    if (this.currentStep < 4) {
      this.currentStep += 1;
      this.persistNotebookCreateDraft();
    }
  }

  previousStep(): void {
    if (this.currentStep === 4 && this.isLocalBrowserInstanceType()) {
      this.currentStep = 1;
      this.clearMyProjectFlowAlert();
      this.persistNotebookCreateDraft();
      this.cdr.detectChanges();
      return;
    }
    if (this.currentStep > 1) {
      this.currentStep -= 1;
      if (this.currentStep === 1) {
        this.clearMyProjectFlowAlert();
      }
      this.persistNotebookCreateDraft();
      this.cdr.detectChanges();
    }
  }

  canShowStepperBack(): boolean {
    return this.currentStep > 1;
  }

  onNotebookNameBlur(): void {
    this.notebookForm.get('name')?.markAsTouched();
  }

  /** Advances step 2 → 3 when the name is valid (Continue button only). */
  onNotebookNameCommit(): void {
    this.notebookForm.get('name')?.markAsTouched();
    if (this.currentStep === 2 && this.notebookForm.get('name')?.valid) {
      this.currentStep = 3;
      this.persistNotebookCreateDraft();
      this.cdr.detectChanges();
    }
  }

  onNotebookInstanceTypeCommit(): void {
    this.notebookForm.get('instanceType')?.markAsTouched();
    if (this.currentStep === 1 && this.notebookForm.get('instanceType')?.value) {
      this.currentStep = this.isLocalBrowserInstanceType() ? 4 : 2;
      this.persistNotebookCreateDraft();
      this.cdr.detectChanges();
    }
  }

  onNotebookComputePlanCommit(): void {
    this.notebookForm.get('computePlanId')?.markAsTouched();
    if (this.currentStep === 3 && this.notebookForm.get('computePlanId')?.value) {
      this.currentStep = 4;
      this.persistNotebookCreateDraft();
      this.cdr.detectChanges();
    }
  }

  getStepSummary(step: number): string {
    switch (step) {
      case 1:
        return this.notebookForm.get('instanceType')?.value ?? '';
      case 2:
        if (this.isLocalBrowserInstanceType()) {
          return 'Not required';
        }
        return this.notebookForm.get('name')?.value ?? '';
      case 3: {
        if (this.isLocalBrowserInstanceType()) {
          return 'Not required';
        }
        const planId = this.notebookForm.get('computePlanId')?.value as string;
        return this.computePlanLookup?.[planId]?.title ?? planId ?? '';
      }
      case 4: {
        if (this.isLocalBrowserInstanceType()) {
          return this.notebookForm.get('policyAgreed')?.value ? 'Policy agreed' : '';
        }
        const date = this.notebookForm.get('scheduleDate')?.value;
        const slots = this.notebookForm.get('scheduleSlots')?.value as string[];
        if (!date) return '';
        const d = new Date(date as string);
        const label = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
        const count = Array.isArray(slots) ? slots.length : 0;
        return `${label} · ${count} session${count !== 1 ? 's' : ''} selected`;
      }
      case 5:
        return this.notebookForm.get('policyAgreed')?.value ? 'Policy agreed' : '';
      default:
        return '';
    }
  }

  /** @deprecated kept for backward compat with template checks */
  get isStep2Unlocked(): boolean { return this.isStepCompleted(1); }
  get isStep3Unlocked(): boolean { return this.isStepCompleted(3); }
  get isStep4Unlocked(): boolean { return this.isStepCompleted(4); }

  // Check individual validation rules
  /** Matches `notebookNameValidator` `invalidCharacters` / allowed charset rule. */
  isLowercaseOnlyValid(): boolean {
    const value = this.notebookForm.get('name')?.value;
    if (!value) return false;
    return /^[a-z0-9-]+$/.test(value);
  }

  /** Min 4 and max 50 characters (validation rules checklist). */
  isNotebookNameLengthValid(): boolean {
    const value = this.notebookForm.get('name')?.value;
    if (!value || typeof value !== 'string') return false;
    return value.length >= 4 && value.length <= this.MAX_NOTEBOOK_NAME_LENGTH;
  }

  isStartsWithLetterOrNumberValid(): boolean {
    const value = this.notebookForm.get('name')?.value;
    if (!value) return false;
    return /^[a-z0-9]/.test(value);
  }

  isHyphenValid(): boolean {
    const value = this.notebookForm.get('name')?.value;
    if (!value) return false;
    return !/^-|-$/.test(value) && !/--/.test(value);
  }

  /**
   * Derive notebook type from storage size: 50Gi → GPU, 10Gi → CPU
   */
  getNotebookTypeFromStorage(storageSize: string): string {
    if (!storageSize || typeof storageSize !== 'string') return '—';
    const normalized = storageSize.trim().replace(/\s+/g, '').toLowerCase();
    if (normalized === '65gi') return 'GPU';
    if (normalized === '10gi') return 'CPU';
    return '—';
  }

  /**
   * Format date to DD-MMM-YYYY format
   */
  formatDate(dateString: string): string {
    if (!dateString) return '';
    
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString; // Return original if invalid
      
      const day = date.getDate().toString().padStart(2, '0');
      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const month = monthNames[date.getMonth()];
      const year = date.getFullYear();
      
      return `${day}-${month}-${year}`;
    } catch (error) {
      console.error('Error formatting date:', error);
      return dateString;
    }
  }
  
  /**
   * Get type chip class for Type column (CPU / GPU)
   */
  getTypeChipClass(type: string): string {
    if (!type) return 'type-chip type-chip-default';
    const t = type.trim().toUpperCase();
    if (t === 'CPU') return 'type-chip type-chip-cpu';
    if (t === 'GPU') return 'type-chip type-chip-gpu';
    return 'type-chip type-chip-default';
  }

  /**
   * Get status chip class based on status value
   */
  getStatusClass(status: string): string {
    const normalizedStatus = (status || '').toLowerCase();
    if (normalizedStatus === 'running') {
      return 'status-chip status-chip--running';
    } else if (normalizedStatus === 'stopped') {
      return 'status-chip status-chip--stopped';
    } else if (normalizedStatus === 'opening') {
      return 'status-chip status-chip--opening';
    }
    return 'status-chip status-chip--default';
  }
  
  /**
   * Check if status is "Opening"
   */
  isOpeningStatus(status: string): boolean {
    if (!status) return false;
    return status.trim().toLowerCase() === 'opening';
  }

  /**
   * Check if status is "Running" (case-insensitive)
   */
  isRunningStatus(status: string): boolean {
    if (!status) return false;
    return status.trim().toLowerCase() === 'running';
  }

  /**
   * Check if status is "Stopped" (case-insensitive)
   */
  isStoppedStatus(status: string): boolean {
    if (!status) return false;
    return status.trim().toLowerCase() === 'stopped';
  }
  
  /**
   * Start polling for notebooks with "Opening" status
   */
  startPollingForOpeningNotebooks(): void {
    // Clear existing intervals first
    this.stopAllPolling();
    
    // Start polling for each notebook with "Opening" status
    this.projectsData.forEach((notebook) => {
      if (this.isOpeningStatus(notebook.status) && notebook.notebookName) {
        this.startStatusPolling(notebook);
      }
    });
  }
  
  /**
   * Start polling status for a specific notebook
   */
  startStatusPolling(notebook: ProjectData): void {
    // Use notebook name instead of ID for the status check API
    const notebookName = notebook.notebookName;
    const notebookId = notebook.id || notebook.notebookName; // Keep ID for tracking intervals
    
    // Don't start polling if already polling for this notebook or if name is missing
    if (!notebookName || this.statusPollingIntervals.has(notebookId)) {
      return;
    }
    
    console.log(`Starting status polling for notebook: ${notebookName}`);
    
    const interval = setInterval(async () => {
      try {
        // Use notebook name for the API call (pass base URL by notebook type for CPU/GPU)
        const baseUrl = this.getSandboxBaseUrlForNotebook(notebook.type);
        const response = await this.sandboxApiService.notebookStatusCheck(notebookName, baseUrl);
        console.log(`Status check response for ${notebookName}:`, response);
        
        // Extract status from response (adjust based on actual API response structure)
        let newStatus = notebook.status;
        let notebookUrl: string | null = null;
        
        if (response?.status) {
          newStatus = response.status;
        } else if (response?.data?.status) {
          newStatus = response.data.status;
        } else if (response?.result?.status) {
          newStatus = response.result.status;
        }
        
        // Extract notebook URL from response if available
        if (response?.url) {
          notebookUrl = response.url;
        } else if (response?.data?.url) {
          notebookUrl = response.data.url;
        } else if (response?.result?.url) {
          notebookUrl = response.result.url;
        } else if (response?.notebookUrl) {
          notebookUrl = response.notebookUrl;
        } else if (response?.data?.notebookUrl) {
          notebookUrl = response.data.notebookUrl;
        } else if (response?.result?.notebookUrl) {
          notebookUrl = response.result.notebookUrl;
        }
        
        // Update the notebook status in projectsData
        const notebookIndex = this.projectsData.findIndex(
          (n) => n.notebookName === notebookName
        );
        
        if (notebookIndex !== -1) {
          const normalizedNewStatus = (newStatus || '').toLowerCase();
          const normalizedOldStatus = (notebook.status || '').toLowerCase();
          
          // Update status if it changed
          if (normalizedNewStatus !== normalizedOldStatus) {
            this.projectsData[notebookIndex].status = newStatus;
            this.rebuildNotebookListCards();
            this.cdr.detectChanges();
            console.log(`Status updated for ${notebookName}: ${normalizedOldStatus} -> ${normalizedNewStatus}`);
            
            // Show confirmation modal when status changes from "Opening" to "Running"
            if (normalizedOldStatus === 'opening' && normalizedNewStatus === 'running') {
              this.showNotebookReadyConfirmation(notebook, notebookUrl);
            }
          }
          
          // Stop polling if status is no longer "Opening"
          if (normalizedNewStatus !== 'opening') {
            this.stopStatusPolling(notebookId);
            console.log(`Stopped polling for ${notebookName} - status is now ${normalizedNewStatus}`);
          }
        }
      } catch (error) {
        console.error(`Error checking status for notebook ${notebookName}:`, error);
        // Stop polling when API response is not 200 (e.g. 404, 5xx)
        this.stopStatusPolling(notebookId);
      }
    }, 10000); // Poll every 10 seconds
    
    this.statusPollingIntervals.set(notebookId, interval);
  }
  
  /**
   * Stop polling status for a specific notebook
   */
  stopStatusPolling(notebookId: string): void {
    const interval = this.statusPollingIntervals.get(notebookId);
    if (interval) {
      clearInterval(interval);
      this.statusPollingIntervals.delete(notebookId);
      console.log(`Stopped status polling for notebook: ${notebookId}`);
    }
  }
  
  /**
   * Stop all polling intervals
   */
  stopAllPolling(): void {
    this.statusPollingIntervals.forEach((interval, notebookId) => {
      clearInterval(interval);
      console.log(`Cleared polling interval for notebook: ${notebookId}`);
    });
    this.statusPollingIntervals.clear();
  }

  private startBookingPolling(): void {
    if (this.activeTab !== 'list') return;
    if (document.visibilityState === 'hidden') return;
    if (this.bookingPollingInterval) return;

    const delay = Math.min(
      this.POLL_BASE_MS * Math.pow(2, this.bookingPollFailures),
      this.POLL_MAX_MS
    );

    this.bookingPollingInterval = setInterval(async () => {
      if (this.activeTab !== 'list' || document.visibilityState === 'hidden') return;
      // Keep row interactions stable while action dropdown/modal is open.
      if (this.openNotebookActionCardId !== null || this.showNotebookExtendModal || this.showNotebookDeleteConfirmModal || this.showNotebookCancelBookingModal) return;
      try {
        this.notebooksLoaded = false;
        await this.loadNotebooks(true);
        // Reset backoff on success
        if (this.bookingPollFailures > 0) {
          this.bookingPollFailures = 0;
          this.stopBookingPolling();
          this.startBookingPolling();
        }
      } catch {
        this.bookingPollFailures++;
        this.stopBookingPolling();
        this.startBookingPolling(); // reschedule with longer delay
      }
    }, delay);
  }

  private stopBookingPolling(): void {
    if (!this.bookingPollingInterval) return;
    clearInterval(this.bookingPollingInterval);
    this.bookingPollingInterval = null;
  }

  private onVisibilityChange(): void {
    if (document.visibilityState === 'hidden') {
      this.stopBookingPolling();
    } else if (this.activeTab === 'list') {
      this.bookingPollFailures = 0;
      this.startBookingPolling();
    }
  }
  
  /**
   * Called when header dispatches userRolesUpdated after a successful role request (e.g. Compute).
   * Refreshes compute role state so "Compute Role Pending" message updates.
   */
  private onUserRolesUpdated(): void {
    this.checkComputeRole();
  }

  ngAfterViewInit(): void {
    this.bindNotebookTableTemplates();
  }

  /**
   * Cleanup on component destroy
   */
  ngOnDestroy(): void {
    this.notebookFormDraftSub?.unsubscribe();
    this.closeNotebookActionDropdown();
    if (typeof window !== 'undefined') {
      window.removeEventListener('userRolesUpdated', this.userRolesUpdatedHandler);
      document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
    }
    this.resetNotebookListFilters(false);
    this.stopAllPolling();
    this.stopBookingPolling();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.openHistoryActionIndex !== null && !target.closest('.action-cell')) {
      this.openHistoryActionIndex = null;
    }
    if (this.notebookActionDropdownIgnoreDocumentClose) return;
    if (
      this.openNotebookActionCardId !== null &&
      !target.closest('.action-cell-wrapper') &&
      !target.closest('.notebook-action-dropdown-overlay') &&
      !target.closest('.action-btn')
    ) {
      this.closeNotebookActionDropdown();
    }
  }

  isNotebookActionDropdownOpen(cardId: string): boolean {
    return this.openNotebookActionCardId === cardId;
  }

  get openNotebookActionCard(): NotebookListCard | null {
    if (!this.openNotebookActionCardId) return null;
    const row = this.notebookTableRows.find(
      (r) => r._card?.id === this.openNotebookActionCardId,
    );
    return row?._card ?? null;
  }

  private getNotebookActionMenuItemCount(card: NotebookListCard): number {
    if (this.showNotebookActiveLifecycleActions(card)) {
      return card.isMock ? 2 : 3;
    }
    if (this.showNotebookCancelBookingAction(card) || this.showNotebookResetBookingAction(card)) {
      return 1;
    }
    return 1;
  }

  /** Row index for app-table z-index when a notebook action menu is open. */
  get openNotebookActionDropdownRowIndex(): number | null {
    if (!this.openNotebookActionCardId) return null;
    const idx = this.notebookTableRows.findIndex(
      (row) => row._card?.id === this.openNotebookActionCardId,
    );
    return idx >= 0 ? idx : null;
  }

  toggleNotebookActionDropdown(event: Event, card: NotebookListCard): void {
    if (this.isNotebookRowActionDisabled(card)) return;

    event.stopPropagation();
    (event as MouseEvent).stopImmediatePropagation?.();
    const nextIsOpen = this.openNotebookActionCardId !== card.id;

    if (!nextIsOpen) {
      this.closeNotebookActionDropdown();
      return;
    }

    const btn = event.currentTarget as HTMLElement;
    this.notebookActionDropdownIgnoreDocumentClose = true;
    this.openNotebookActionCardId = card.id;
    this.notebookActionDropdownOverlay.open({
      anchor: btn,
      itemCount: this.getNotebookActionMenuItemCount(card),
      onPosition: (pos) => {
        this.notebookActionOverlayPosition = pos;
        this.cdr.markForCheck();
      },
      onAnchorDetached: () => this.closeNotebookActionDropdown(),
    });
    this.cdr.detectChanges();
    setTimeout(() => {
      this.notebookActionDropdownIgnoreDocumentClose = false;
    }, 0);
  }

  closeNotebookActionDropdown(): void {
    this.notebookActionDropdownOverlay.close();
    this.openNotebookActionCardId = null;
    this.notebookActionOverlayPosition = null;
    this.cdr.detectChanges();
  }

  async handleAction(action: string, row: ProjectData, mockDisplayTitle?: string): Promise<void> {
    switch (action) {
      case 'open':
        await this.openNotebook(row, mockDisplayTitle);
        break;
      case 'stop':
        await this.stopNotebook(row);
        break;
      case 'start':
        await this.startNotebook(row);
        break;
      case 'delete':
        await this.deleteNotebook(row);
        break;
      default:
        console.log('Unknown action:', action, row);
    }
  }

  /**
   * Stop a notebook — returns whether the sandbox API succeeded
   */
  async stopNotebook(row: ProjectData): Promise<boolean> {
    try {
      // Prepare payload with notebook name
      const payload = {
        name: row.notebookName
      };
      
      console.log('Stopping notebook with payload:', payload);
      const baseUrl = this.getSandboxBaseUrlForNotebook(row.type);
      const response = await this.sandboxApiService.stopNotebook(payload, baseUrl);
      console.log('Notebook stopped successfully:', response);
      this.googleAnalytics.trackMyProjectNotebookStop(row.notebookName);

      // Refresh the notebook list after successful stop
      this.notebooksLoaded = false;
      await this.loadNotebooks();
      return true;
    } catch (error) {
      console.error('Error stopping notebook:', error);
      this.showMyProjectFlowError('Failed to stop notebook. Please try again.');
      return false;
    }
  }

  /**
   * Start a notebook
   */
  async startNotebook(row: ProjectData): Promise<void> {
    try {
      // Prepare payload with notebook name
      const payload = {
        name: row.notebookName
      };
      
      console.log('Starting notebook with payload:', payload);
      const baseUrl = this.getSandboxBaseUrlForNotebook(row.type);
      const response = await this.sandboxApiService.startNotebook(payload, baseUrl);
      console.log('Notebook started successfully:', response);
      this.googleAnalytics.trackMyProjectNotebookStart(row.notebookName);

      // Update the status locally to "Opening" immediately for better UX
      const notebookIndex = this.projectsData.findIndex(
        (n) => n.notebookName === row.notebookName
      );
      if (notebookIndex !== -1) {
        this.projectsData[notebookIndex].status = 'Opening';
        this.rebuildNotebookListCards();
        this.cdr.detectChanges();

        // Start polling for this notebook to track status changes
        this.startStatusPolling(this.projectsData[notebookIndex]);
      }
      
      // Optionally refresh the notebook list after a delay to get updated status
      // But don't show loading skeleton - let the status update naturally
      setTimeout(async () => {
        this.notebooksLoaded = false;
        await this.loadNotebooks();
      }, 2000); // Refresh after 2 seconds to get updated status
    } catch (error) {
      console.error('Error starting notebook:', error);
      this.showMyProjectFlowError('Failed to start notebook. Please try again.');
    }
  }

  /**
   * Delete a notebook — returns whether the sandbox API succeeded
   */
  async deleteNotebook(row: ProjectData): Promise<boolean> {
    try {
      // Prepare payload with notebook name
      const payload = {
        name: row.notebookName
      };
      
      console.log('Deleting notebook with payload:', payload);
        const baseUrl = this.getSandboxBaseUrlForNotebook(row.type);
        const response = await this.sandboxApiService.deleteNotebook(payload, baseUrl);
        console.log('Notebook deleted successfully:', response);
        this.googleAnalytics.trackMyProjectNotebookDelete(row.notebookName);

        // Remove the notebook from the local array immediately for better UX
      this.projectsData = this.projectsData.filter(
        (n) => n.notebookName !== row.notebookName
      );
      this.rebuildNotebookListCards();
      this.cdr.detectChanges();
      
      // Show success toaster message
      this.toasterService.success("You've successfully deleted the Notebook");
      
      // Optionally refresh the notebook list to ensure consistency
      setTimeout(async () => {
        this.notebooksLoaded = false;
        await this.loadNotebooks();
      }, 500);
      return true;
    } catch (error) {
      console.error('Error deleting notebook:', error);
      this.showMyProjectFlowError('Failed to delete notebook. Please try again.');
      // Refresh list on error to ensure consistency
      this.notebooksLoaded = false;
      await this.loadNotebooks();
      return false;
    }
  }

  createNotebook(): void {
    void this.setActiveTab('create');
  }

  /** Loads the create wizard. */
  private async prepareCreateNotebookTab(options?: { preserveDraft?: boolean }): Promise<void> {
    const preserveDraft = options?.preserveDraft ?? this.hasNotebookCreateDraft();
    if (!preserveDraft) {
      this.clearNotebookCreateDraft();
      this.initNotebookForm();
      this.currentStep = 1;
    }
    await this.loadCategoryInstanceTypes();
    if (preserveDraft) {
      await this.rehydrateNotebookScheduleAfterDraftRestore();
      this.persistNotebookCreateDraft();
      this.cdr.markForCheck();
    }
  }

  clearMyProjectFlowAlert(): void {
    if (!this.myProjectFlowAlert) {
      return;
    }
    this.myProjectFlowAlert = null;
    this.cdr.markForCheck();
  }

  private showMyProjectFlowError(message: string): void {
    this.myProjectFlowAlert = { type: 'error', message };
    this.cdr.markForCheck();
  }

  private showMyProjectFlowInfo(message: string): void {
    this.myProjectFlowAlert = { type: 'info', message };
    this.cdr.markForCheck();
  }

  private extractApiErrorDetail(error: unknown): string {
    const err = error as {
      error?: { detail?: string; message?: string };
      detail?: string;
      message?: string;
    };
    const detail =
      err?.error?.detail ??
      err?.detail ??
      err?.error?.message ??
      err?.message ??
      '';
    return typeof detail === 'string' ? detail.trim() : '';
  }

  private formatNotebookCreateErrorMessage(detail: string): string {
    if (!detail) {
      return 'Failed to create notebook. Please try again.';
    }

    const normalized = detail.toLowerCase();
    if (
      normalized.includes('max contiguous slot') ||
      normalized.includes('exceed max contiguous') ||
      normalized.includes('contiguous slot selection limit')
    ) {
      return this.maxContiguousScheduleSlotsMessage;
    }

    return `Failed to create notebook: ${detail}`;
  }

  private isCreditLimitExceededError(detail: string): boolean {
    return detail.toLowerCase().includes('credit limit');
  }

  async setActiveTab(tabKey: 'create' | 'list' | 'request-credits'): Promise<void> {
    if (tabKey === 'request-credits' && !this.requestCreditsEnabled) {
      return;
    }

    const previousTab = this.activeTab;
    if (previousTab !== tabKey) {
      this.resetNotebookListFilters(false);
      this.clearMyProjectFlowAlert();
    }

    // Prevent switching to request-credits if user doesn't have compute role (neither active nor pending)
    if (tabKey === 'request-credits' && !this.hasComputeRole) {
      console.log('Cannot access Request Credits tab: Compute role required');
      // Still allow switching to show the message
      this.activeTab = tabKey;
      this.updateTabQueryParam(tabKey);
      return;
    }
    
    if (previousTab === 'create' && tabKey !== 'create') {
      this.clearNotebookCreateDraft();
    }

    this.activeTab = tabKey;
    this.updateTabQueryParam(tabKey);
    if (tabKey === 'create' && previousTab !== 'create') {
      // Tab switch back to Create always starts at step 1 (draft restore is for page refresh only).
      await this.prepareCreateNotebookTab({ preserveDraft: false });
    }

    // Always refresh notebook list when user opens Notebook List tab.
    if (tabKey === 'list') {
      this.notebooksLoaded = false;
      await this.loadNotebooks();
      this.scheduleNotebookTableTemplateBind();
      this.startBookingPolling();
    } else {
      this.stopBookingPolling();
    }

    // When switching to request-credits, refresh pending request check (only when user has active Compute)
    if (tabKey === 'request-credits' && this.hasActiveComputeRole) {
      this.checkPendingCreditRequest();
    }
  }

  /** Update URL query param so the active tab persists on refresh */
  private updateTabQueryParam(tabKey: 'create' | 'list' | 'request-credits'): void {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabKey },
      queryParamsHandling: 'merge',
      replaceUrl: true
    });
  }

  /**
   * Load credit balance from API
   */
  async loadCreditBalance(): Promise<void> {
    this.creditsLoading = true;
    try {
      const response = await this.authApiService.getCreditBalance();
      console.log('Credit balance API response:', response);
      
      // Support both known payload shapes:
      // 1) { result: { balance: number, expiration_date: string, isValid: boolean } }
      // 2) { result: { balance: { balance: number, expiration_date: string, isValid: boolean } } }
      const result = response?.result ?? {};
      const balanceData =
        result?.balance && typeof result.balance === 'object'
          ? result.balance
          : result;

      if (balanceData && typeof balanceData === 'object') {
        const rawBalance = balanceData.balance;
        if (rawBalance !== undefined && rawBalance !== null && rawBalance !== '') {
          const parsedBalance = Number(rawBalance);
          this.creditsRemaining = Number.isFinite(parsedBalance) ? parsedBalance : 0;
        } else {
          this.creditsRemaining = 0;
        }

        this.creditsExpirationDate =
          balanceData.expiration_date ?? balanceData.expirationDate ?? null;

        // If API explicitly tells validity, prefer that; otherwise derive from date.
        if (typeof balanceData.isValid === 'boolean') {
          this.creditsExpired = !balanceData.isValid;
        } else {
          this.creditsExpired = this.isCreditsExpired();
        }
      }
      this.creditsLoading = false;
    } catch (error: any) {
      console.error('Error loading credit balance:', error);
      this.creditsLoading = false;
      
      // Check if it's a 403 error with the specific message about GPU compute role
      const errorStatus = error?.status || error?.error?.status || error?.error?.error?.status;
      const errorDetail = error?.error?.detail || error?.detail || error?.error?.error?.detail || '';
      const errorType = error?.error?.type || error?.type || error?.error?.error?.type || '';
      
      if (errorStatus === 403 || errorType === 'dx:controlPlane:forbidden') {
        if (errorDetail.includes('Please upgrade your role to access GPU-based compute')) {
          // Set credits to 0 and expiration to NA for forbidden access
          this.creditsRemaining = 0;
          this.creditsExpirationDate = 'NA';
          this.creditsExpired = false;
          console.log('Access forbidden - setting credits to 0 and expiration to NA');
          return;
        }
      }
      
      // Keep default values on other errors
    }
  }

  /**
   * Check if user already has a pending credit request (GET user/credit/request).
   * Sets hasPendingCreditRequest so the UI can show a message or disable duplicate requests.
   */
  async checkPendingCreditRequest(): Promise<void> {
    this.pendingCreditRequestLoading = true;
    this.pendingCreditRequestData = null;
    this.pendingCreditRequestId = null;
    try {
      const response = await this.authApiService.getUserCreditRequest(1, 10);
      const list: any[] = response?.result ?? response?.data ?? response?.items ?? (Array.isArray(response) ? response : []);
      const pendingItem = list.find(
        (item: any) => String(item?.status ?? '').toLowerCase() === 'pending'
      );
      this.hasPendingCreditRequest = !!pendingItem;
      if (pendingItem) {
        this.pendingCreditRequestData = pendingItem.additionalInfo ?? pendingItem.additional_info ?? pendingItem;
        this.pendingCreditRequestId = pendingItem.id ?? pendingItem.requestId ?? null;
      }
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error checking pending credit request:', error);
      this.hasPendingCreditRequest = false;
      this.pendingCreditRequestData = null;
      this.pendingCreditRequestId = null;
    } finally {
      this.pendingCreditRequestLoading = false;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  openWithdrawCreditModal(): void {
    this.showWithdrawCreditModal = true;
  }

  closeWithdrawCreditModal(): void {
    this.showWithdrawCreditModal = false;
    this.withdrawCreditLoading = false;
  }

  openRequestCreditsHistoryModal(): void {
    this.showRequestCreditsHistoryModal = true;
    this.loadCreditRequestHistory();
  }

  closeRequestCreditsHistoryModal(): void {
    this.showRequestCreditsHistoryModal = false;
    this.openHistoryActionIndex = null;
  }

  toggleHistoryActionDropdown(event: Event, index: number): void {
    event.stopPropagation();
    const target = event.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    
    this.openHistoryActionIndex = this.openHistoryActionIndex === index ? null : index;
    
    // Trigger change detection immediately
    this.cdr.detectChanges();
    
    // Set dropdown position using fixed positioning
    if (this.openHistoryActionIndex === index) {
      setTimeout(() => {
        const dropdown = document.querySelector('.action-dropdown') as HTMLElement;
        if (dropdown) {
          dropdown.style.top = `${rect.bottom + 4}px`;
          dropdown.style.left = `${rect.left}px`;
        }
      }, 0);
    }
  }

  fillFromHistory(row: { requestedOn: string; status: string; id?: string; originalData?: any }): void {
    // Close the dropdown and modal
    this.openHistoryActionIndex = null;
    this.showRequestCreditsHistoryModal = false;

    // Get additionalInfo from originalData
    const additionalInfo = row.originalData?.additionalInfo ?? row.originalData?.additional_info ?? row.originalData;
    
    // Pass to request-credits component to auto-fill the form
    if (this.requestCreditsComponent) {
      this.requestCreditsComponent.fillFormFromHistory(additionalInfo);
    }
  }

  async loadCreditRequestHistory(): Promise<void> {
    this.creditRequestHistoryLoading = true;
    this.creditRequestHistory = [];
    try {
      const response = await this.authApiService.getUserCreditRequest(1, 100, 'requestedAt:desc');
      const list: any[] = response?.result ?? response?.data ?? response?.items ?? (Array.isArray(response) ? response : []);
      this.creditRequestHistory = list.map((item: any) => {
        const requestedAt = item.requestedAt ?? item.requested_at ?? '';
        const status = (item.status ?? 'N/A').trim();
        const statusFormatted = status ? status.charAt(0).toUpperCase() + status.slice(1).toLowerCase() : 'N/A';
        return {
          requestedOn: requestedAt ? this.formatDate(requestedAt) : 'N/A',
          status: statusFormatted,
          id: item.id ?? item.requestId,
          originalData: item,
        };
      });
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error loading credit request history:', error);
      this.creditRequestHistory = [];
    } finally {
      this.creditRequestHistoryLoading = false;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  getStatusChipClass(status: string): string {
    if (!status) return 'status-chip status-chip-default';
    const s = status.toLowerCase();
    if (s === 'pending' || s === 'pending approval') return 'status-chip status-chip-pending';
    if (s === 'granted' || s === 'approved') return 'status-chip status-chip-granted';
    if (s === 'rejected') return 'status-chip status-chip-rejected';
    return 'status-chip status-chip-default';
  }

  /**
   * Called when the user successfully submits the Request Credits form.
   * Refreshes pending state so the pending warning div with withdraw button is shown.
   */
  async onCreditRequestSubmitted(): Promise<void> {
    this.googleAnalytics.trackMyProjectRequestCredits();
    await this.checkPendingCreditRequest();
  }

  async confirmWithdrawCreditRequest(): Promise<void> {
    const id = this.pendingCreditRequestId;
    if (!id) {
      this.showMyProjectFlowError('Request ID is missing. Cannot withdraw.');
      this.closeWithdrawCreditModal();
      return;
    }
    this.withdrawCreditLoading = true;
    try {
      await this.authApiService.withdrawUserCreditRequest(id);
      this.toasterService.success('Credit request has been withdrawn successfully.');
      this.closeWithdrawCreditModal();
      await this.checkPendingCreditRequest();
    } catch (error) {
      console.error('Error withdrawing credit request:', error);
      this.showMyProjectFlowError('Failed to withdraw credit request. Please try again.');
    } finally {
      this.withdrawCreditLoading = false;
      this.cdr.markForCheck();
      this.cdr.detectChanges();
    }
  }

  /**
   * Check if credits are expired based on expiration date
   */
  isCreditsExpired(): boolean {
    if (!this.creditsExpirationDate) {
      return false;
    }
    const expiryDate = new Date(this.creditsExpirationDate);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expiryDate.setHours(0, 0, 0, 0);
    return expiryDate < today;
  }

  onModalCancel(): void {
    this.showCreateNotebookModal = false;
    this.notebookForm.reset();
    this.clearNotebookCreateDraft();
    this.initNotebookForm();
    this.currentStep = 1;
  }

  onModalClose(): void {
    this.showCreateNotebookModal = false;
    this.notebookForm.reset();
    this.clearNotebookCreateDraft();
    this.initNotebookForm();
    this.currentStep = 1;
  }

  /** Plans shown after user picks CPU or GPU. */
  getComputePlansForCurrentInstanceType(): NotebookComputePlanOption[] {
    const kind = this.notebookForm?.get('instanceType')?.value as 'CPU' | 'GPU' | undefined;
    if (!kind) return [];
    return this.computePlans.filter((p) => p.apiType === kind);
  }

  private getTodayScheduleDateKey(): string {
    return this.formatNotebookScheduleDateKey(new Date());
  }

  /** Resets the selected schedule date and slots back to today so the calendar opens fresh. */
  private resetScheduleDate(): void {
    const today = new Date();
    this.scheduleCalendarAnchor = new Date(today.getFullYear(), today.getMonth(), 1);
    this.notebookForm.patchValue({ scheduleDate: this.getTodayScheduleDateKey(), scheduleSlots: [] });
    this.notebookForm.get('scheduleDate')?.markAsUntouched();
    this.notebookForm.get('scheduleSlots')?.markAsUntouched();
    this.availableSlots = [];
    this.calendarAvailability = {};
  }

  selectNotebookInstanceKind(value: string): void {
    if (value === 'GPU' && this.isGpuDisabled()) return;
    const prev = this.notebookForm.get('instanceType')?.value;
    this.notebookForm.patchValue({
      instanceType: value,
      computePlanId: '',
      userCategory: '',
    });
    this.userCategoryOptions = [];
    this.gpuInstanceTypes = [];
    this.notebookForm.get('instanceType')?.markAsTouched();
    this.applyNotebookFormValidatorsForInstanceType(value);
    if (this.isLocalBrowserInstanceType(value) && (this.currentStep === 2 || this.currentStep === 3)) {
      this.currentStep = 4;
    } else if (!this.isLocalBrowserInstanceType(value) && this.currentStep === 4 && !this.isStepCompleted(2)) {
      this.currentStep = 2;
    }
    this.cdr.detectChanges();
    if (prev !== value) {
      this.clearMyProjectFlowAlert();
      this.notebookForm.get('computePlanId')?.markAsUntouched();
      this.resetScheduleDate();
    }
  }

  private applyNotebookFormValidatorsForInstanceType(value: string): void {
    const isLocal = this.isLocalBrowserInstanceType(value);
    const name = this.notebookForm.get('name');
    const computePlanId = this.notebookForm.get('computePlanId');
    const scheduleDate = this.notebookForm.get('scheduleDate');
    const scheduleSlots = this.notebookForm.get('scheduleSlots');
    if (isLocal) {
      name?.clearValidators();
      computePlanId?.clearValidators();
      scheduleDate?.clearValidators();
      scheduleSlots?.clearValidators();
      computePlanId?.setValue('');
      scheduleDate?.setValue('');
      scheduleSlots?.setValue([]);
    } else {
      name?.setValidators([Validators.required, this.notebookNameValidator]);
      computePlanId?.setValidators([Validators.required]);
      scheduleDate?.setValidators([Validators.required]);
      scheduleSlots?.setValidators([this.scheduleSlotsValidatorFn]);
    }
    name?.updateValueAndValidity({ emitEvent: false });
    computePlanId?.updateValueAndValidity({ emitEvent: false });
    scheduleDate?.updateValueAndValidity({ emitEvent: false });
    scheduleSlots?.updateValueAndValidity({ emitEvent: false });
  }

  private mergeInstanceTypeOptionsWithLocal(
    options: { label: string; value: string }[],
  ): { label: string; value: string }[] {
    const merged = options.filter((o) => o.value !== this.localBrowserInstanceType);
    if (this.isBrowserLaunchAvailable()) {
      merged.push({
        label: this.browserCategory?.displayName ?? 'Local (Browser)',
        value: this.localBrowserInstanceType,
      });
    }
    return merged;
  }

  private async loadCategoryInstanceTypes(): Promise<void> {
    try {
      const baseUrl = this.getSandboxBaseUrlForNotebook('CPU');
      const response = await this.sandboxApiService.listCategories(baseUrl);
      const categories = this.extractCategoryItems(response);
      this.browserCategory = this.resolveBrowserCategory(categories);
      const mappedPlans = this.mapCategoriesToComputePlans(categories);
      const resourceTypes = Array.from(
        new Set(
          mappedPlans
            .map((plan) => plan.apiType)
            .filter((resourceType) => resourceType === 'CPU' || resourceType === 'GPU'),
        ),
      );

      this.computePlans = mappedPlans.length > 0 ? mappedPlans : [...this.defaultComputePlans];
      this.buildComputePlanLookup();

      if (resourceTypes.length > 0) {
        this.instanceTypeOptions = this.mergeInstanceTypeOptionsWithLocal(
          resourceTypes.map((resourceType) => ({
            label: resourceType,
            value: resourceType,
          })),
        );
      } else {
        this.instanceTypeOptions = this.mergeInstanceTypeOptionsWithLocal([
          ...this.defaultInstanceTypeOptions,
        ]);
      }

      const selectedType = (this.notebookForm.get('instanceType')?.value ?? '').toUpperCase();
      if (
        selectedType &&
        selectedType !== this.localBrowserInstanceType &&
        !resourceTypes.includes(selectedType)
      ) {
        this.notebookForm.patchValue({ instanceType: '', computePlanId: '', userCategory: '' });
      }
      this.cdr.markForCheck();

      // Pre-load calendar for the first available plan so the calendar is populated on page entry
      const firstPlan = this.computePlans[0];
      if (firstPlan?.id) {
        void this.loadCalendarSlots(firstPlan.apiCategory ?? firstPlan.id.replace(/-/g, '_'));
      }
    } catch (error) {
      console.error('Error loading sandbox categories:', error);
      this.computePlans = [...this.defaultComputePlans];
      this.buildComputePlanLookup();
      this.browserCategory = null;
      this.instanceTypeOptions = this.mergeInstanceTypeOptionsWithLocal([
        ...this.defaultInstanceTypeOptions,
      ]);
    }
  }

  private resolveBrowserCategory(
    categories: SandboxCategoryResource[],
  ): SandboxBrowserCategory | null {
    for (const category of categories) {
      const resourceType = (category?.resourceType ?? category?.resource_type ?? category?.type ?? '')
        .toString()
        .trim()
        .toLowerCase();
      const launchUrl = (category?.launchUrl ?? category?.launch_url ?? '').toString().trim();
      if (!launchUrl) continue;

      const launchMode = (category?.launchMode ?? category?.launch_mode ?? '')
        .toString()
        .trim()
        .toLowerCase();

      if (resourceType === 'browser' || launchMode === 'direct') {
        return {
          launchUrl,
          displayName:
            category?.displayName?.trim() ||
            category?.display_name?.trim() ||
            category?.title?.trim() ||
            category?.name?.trim(),
          description: category?.description?.trim(),
        };
      }
    }
    return null;
  }

  private extractCategoryItems(response: any): SandboxCategoryResource[] {
    const collected: SandboxCategoryResource[] = [];

    const readResourceType = (candidate: SandboxCategoryResource): string =>
      (candidate?.resourceType ?? candidate?.resource_type ?? candidate?.type ?? '')
        .toString()
        .trim()
        .toUpperCase();

    const walk = (value: any): void => {
      if (!value) return;
      if (typeof value === 'string') {
        const asType = value.trim().toUpperCase();
        if (asType === 'CPU' || asType === 'GPU' || asType === 'BROWSER') {
          collected.push({ resourceType: asType });
        }
        return;
      }
      if (Array.isArray(value)) {
        value.forEach((item) => walk(item));
        return;
      }
      if (typeof value !== 'object') return;

      const resourceType = readResourceType(value);
      if (resourceType === 'CPU' || resourceType === 'GPU' || resourceType === 'BROWSER') {
        collected.push(value as SandboxCategoryResource);
      }

      const nestedKeys = [
        'result',
        'results',
        'data',
        'items',
        'categories',
        'category',
        'resources',
        'resource',
        'resourceTypes',
        'resource_types',
        'configuration',
        'configurations',
        'list',
        'records',
      ] as const;

      nestedKeys.forEach((key) => {
        if (key in value) {
          walk((value as any)[key]);
        }
      });
    };

    walk(response);
    return collected;
  }

  private mapCategoriesToComputePlans(categories: SandboxCategoryResource[]): NotebookComputePlanOption[] {
    const plans = categories
      .map((category, index) => {
        const apiType = (category?.resourceType ?? category?.resource_type ?? category?.type ?? '').trim().toUpperCase();
        if (apiType !== 'CPU' && apiType !== 'GPU') {
          return null;
        }

        const rawTitle =
          category?.name?.trim() ||
          category?.title?.trim() ||
          category?.category?.trim() ||
          `${apiType} Compute`;
        const title = this.formatComputePlanDisplayTitle(rawTitle);

        const hardwareParts = [category?.cpu, category?.memory, category?.storage, category?.gpu]
          .filter((item): item is string => typeof item === 'string' && item.trim().length > 0)
          .map((item) => item.trim());
        const cardMeta = hardwareParts.length > 0 ? hardwareParts.join(' · ') : undefined;
        const bookingLimits = [
          this.toBookingLimitItem('Max active bookings', category?.maxActiveBookings ?? category?.max_active_bookings),
          this.toBookingLimitItem('Max bookings / week', category?.maxBookingsPerWeek ?? category?.max_bookings_per_week),
          this.toBookingLimitItem('Max Concurrent users', category?.maxConcurrentUsers ?? category?.max_concurrent_users),
        ].filter((item): item is { label: string; value: string } => !!item);

        const idCandidate =
          category?.id?.trim() ||
          rawTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '') ||
          `${apiType.toLowerCase()}-plan-${index + 1}`;

        const icon: NotebookComputePlanOption['icon'] =
          apiType === 'GPU' ? (index % 2 === 0 ? 'layers' : 'display') : (hardwareParts.some((spec) => /\b(32|64|128)\s*gb\b/i.test(spec)) ? 'memory' : 'chip');

        return {
          id: idCandidate,
          apiCategory:
            category?.category?.trim() ||
            category?.id?.trim()?.replace(/-/g, '_') ||
            idCandidate.replace(/-/g, '_'),
          title,
          description: category?.description?.trim() || `Compute category for ${apiType} workloads.`,
          cardMeta,
          bookingLimits: bookingLimits.length > 0 ? bookingLimits : undefined,
          apiType,
          sandboxGpuInstance:
            apiType === 'GPU'
              ? category?.instanceType?.trim() || category?.gpuInstanceType?.trim() || category?.gpuInstance?.trim() || 'g4dn.xlarge'
              : undefined,
          icon,
        } as NotebookComputePlanOption;
      })
      .filter((plan): plan is NotebookComputePlanOption => !!plan);

    const uniqueById = new Map<string, NotebookComputePlanOption>();
    plans.forEach((plan) => {
      if (!uniqueById.has(plan.id)) {
        uniqueById.set(plan.id, plan);
      }
    });
    return Array.from(uniqueById.values());
  }

  /** e.g. `cpu_basic` → `Cpu Basic`; leaves already-formatted titles unchanged. */
  formatComputePlanDisplayTitle(raw: string): string {
    const trimmed = (raw ?? '').trim();
    if (!trimmed || !trimmed.includes('_')) {
      return trimmed;
    }
    return trimmed
      .split('_')
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
      .join(' ');
  }

  private toBookingLimitItem(label: string, value: number | string | undefined): { label: string; value: string } | null {
    if (value === undefined || value === null) return null;
    const normalized = String(value).trim();
    if (!normalized) return null;
    return { label, value: normalized };
  }

  private buildComputePlanLookup(): void {
    this.computePlanLookup = this.computePlans.reduce(
      (acc, plan) => {
        acc[plan.id] = plan;
        return acc;
      },
      {} as Record<string, NotebookComputePlanOption>,
    );
  }

  selectComputePlan(planId: string): void {
    const plan = this.computePlanLookup?.[planId];
    if (!plan) return;
    const kind = this.notebookForm.get('instanceType')?.value;
    if (plan.apiType !== kind) return;
    if (plan.apiType === 'GPU' && this.isGpuDisabled()) return;

    const prevPlanId = this.notebookForm.get('computePlanId')?.value;
    this.notebookForm.patchValue({
      computePlanId: planId,
      userCategory: '',
    });
    this.userCategoryOptions = [];
    this.gpuInstanceTypes = [];
    this.notebookForm.get('computePlanId')?.markAsTouched();

    // Reset the selected date whenever the category actually changes
    if (prevPlanId !== planId) {
      this.resetScheduleDate();
    }

    void this.loadCalendarSlots(plan.apiCategory ?? plan.id.replace(/-/g, '_'));
    void this.loadAvailableSlots(this.getTodayScheduleDateKey());
    this.cdr.detectChanges();
  }

  advanceScheduleCalendarMonth(delta: number): void {
    const d = new Date(this.scheduleCalendarAnchor);
    d.setMonth(d.getMonth() + delta);
    this.scheduleCalendarAnchor = new Date(d.getFullYear(), d.getMonth(), 1);
    this.persistNotebookCreateDraft();
    if (this.notebookForm.get('computePlanId')?.value) {
      const selectedPlanId = this.notebookForm.get('computePlanId')?.value as string;
      const selectedPlan = this.computePlanLookup?.[selectedPlanId];
      this.calendarAvailability = {};
      void this.loadCalendarSlots(selectedPlan?.apiCategory ?? selectedPlanId.replace(/-/g, '_'));
    }
  }

  get scheduleCalendarTitle(): string {
    return new Intl.DateTimeFormat('en-US', { month: 'long', year: 'numeric' }).format(this.scheduleCalendarAnchor);
  }

  formatNotebookScheduleDateKey(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  getScheduleCalendarWeeks(): NotebookCalendarCell[][] {
    const anchor = this.scheduleCalendarAnchor;
    const year = anchor.getFullYear();
    const month = anchor.getMonth();
    const startPad = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();

    const cells: NotebookCalendarCell[] = [];
    for (let i = 0; i < startPad; i++) {
      cells.push({ date: null, key: null, inMonth: false });
    }
    for (let d = 1; d <= daysInMonth; d++) {
      const date = new Date(year, month, d);
      cells.push({
        date,
        key: this.formatNotebookScheduleDateKey(date),
        inMonth: true,
      });
    }
    while (cells.length % 7 !== 0) {
      cells.push({ date: null, key: null, inMonth: false });
    }
    while (cells.length < 42) {
      cells.push({ date: null, key: null, inMonth: false });
    }

    const weeks: NotebookCalendarCell[][] = [];
    for (let i = 0; i < cells.length; i += 7) {
      weeks.push(cells.slice(i, i + 7));
    }
    return weeks;
  }

  isNotebookSchedulePastDate(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const t = new Date(date);
    t.setHours(0, 0, 0, 0);
    return t.getTime() < today.getTime();
  }

  /** Returns true when the date is more than 30 days from today. */
  isNotebookScheduleBeyondWindow(date: Date): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const limit = new Date(today);
    limit.setDate(limit.getDate() + 30);
    const t = new Date(date);
    t.setHours(0, 0, 0, 0);
    return t.getTime() > limit.getTime();
  }

  /** True when the calendar is already showing the current month — back nav is pointless. */
  get isCalendarBackDisabled(): boolean {
    const today = new Date();
    return (
      this.scheduleCalendarAnchor.getFullYear() === today.getFullYear() &&
      this.scheduleCalendarAnchor.getMonth() === today.getMonth()
    );
  }

  /** True when the first day of the next month is already beyond the 30-day booking window. */
  get isCalendarForwardDisabled(): boolean {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const limit = new Date(today);
    limit.setDate(limit.getDate() + 30);
    const nextMonthStart = new Date(
      this.scheduleCalendarAnchor.getFullYear(),
      this.scheduleCalendarAnchor.getMonth() + 1,
      1
    );
    return nextMonthStart.getTime() > limit.getTime();
  }

  isNotebookScheduleDayFullyBooked(dateKey: string): boolean {
    if (!(dateKey in this.calendarAvailability)) return false;
    return !this.calendarAvailability[dateKey];
  }

  private async loadCalendarSlots(categoryId?: string): Promise<void> {
    const selectedPlanId = this.notebookForm.get('computePlanId')?.value as string | undefined;
    const selectedPlan = selectedPlanId ? this.computePlanLookup?.[selectedPlanId] : undefined;
    const calendarCategory = (categoryId ?? selectedPlan?.apiCategory ?? selectedPlanId ?? '')
      .toString()
      .trim();
    if (!calendarCategory) return;

    const anchor = this.scheduleCalendarAnchor;
    const month = `${anchor.getFullYear()}-${String(anchor.getMonth() + 1).padStart(2, '0')}`;
    const baseUrl = this.getSandboxBaseUrlForNotebook(
      this.notebookForm.get('instanceType')?.value ?? 'CPU',
    );

    this.calendarLoading = true;
    this.cdr.markForCheck();

    try {
      const response = await this.sandboxApiService.listCalendarSlots(
        calendarCategory,
        month,
        baseUrl,
      );
      this.calendarAvailability = this.extractCalendarAvailability(response);
    } catch (error) {
      console.error('Error loading calendar slots:', error);
      this.calendarAvailability = {};
    } finally {
      this.calendarLoading = false;
      this.cdr.markForCheck();
    }
  }

  private extractCalendarAvailability(response: any): Record<string, boolean> {
    const items: any[] = Array.isArray(response)
      ? response
      : Array.isArray(response?.days)
        ? response.days
        : Array.isArray(response?.result?.days)
          ? response.result.days
          : Array.isArray(response?.data?.days)
            ? response.data.days
            : Array.isArray(response?.result)
              ? response.result
              : Array.isArray(response?.results)
                ? response.results
                : Array.isArray(response?.data)
                  ? response.data
                  : Array.isArray(response?.items)
                    ? response.items
                    : [];

    const map: Record<string, boolean> = {};
    items.forEach((item: any) => {
      const dateKey = item?.date as string | undefined;
      const rawAvailability =
        item?.hasAvailability ?? item?.hasVisibality ?? item?.hasVisibility;
      const parsedAvailability =
        typeof rawAvailability === 'boolean'
          ? rawAvailability
          : typeof rawAvailability === 'string'
            ? rawAvailability.trim().toLowerCase() === 'true'
            : undefined;

      if (dateKey && typeof parsedAvailability === 'boolean') {
        map[dateKey] = parsedAvailability;
      }
    });
    return map;
  }

  notebookScheduleAvailabilityDotClass(dateKey: string | null, inMonth: boolean): '' | 'booked' | 'open' {
    if (!dateKey || !inMonth) return '';
    if (!(dateKey in this.calendarAvailability)) return '';
    // Parse the dateKey back to a Date for range checks
    const d = new Date(dateKey + 'T00:00:00');
    if (this.isNotebookSchedulePastDate(d)) return '';
    if (this.isNotebookScheduleBeyondWindow(d)) return '';
    return this.calendarAvailability[dateKey] ? 'open' : 'booked';
  }

  pickNotebookScheduleCalendarCell(cell: NotebookCalendarCell): void {
    if (!cell.inMonth || !cell.date || !cell.key) return;
    if (this.isNotebookSchedulePastDate(cell.date)) return;
    if (this.isNotebookScheduleDayFullyBooked(cell.key)) return;

    const curKey = this.notebookForm?.get('scheduleDate')?.value;
    const isSameDate = typeof curKey === 'string' && curKey === cell.key;
    const nextSlots = isSameDate
      ? ((this.notebookForm.get('scheduleSlots')?.value as string[]) ?? [])
      : [];

    this.notebookForm.patchValue({
      scheduleDate: cell.key,
      scheduleSlots: nextSlots,
    });
    this.notebookForm.get('scheduleDate')?.markAsTouched();
    if (!isSameDate) {
      this.notebookForm.get('scheduleSlots')?.markAsUntouched();
      this.availableSlots = [];
    }

    // Always refresh slots API on date click (even when clicking same date again).
    void this.loadAvailableSlots(cell.key);
  }

  isNotebookScheduleCalendarCellSelected(cell: NotebookCalendarCell): boolean {
    const sd = this.notebookForm?.get('scheduleDate')?.value;
    return !!sd && !!cell.key && sd === cell.key;
  }

  isNotebookScheduleTodayKey(key: string | null): boolean {
    if (!key) return false;
    const t = new Date();
    return key === this.formatNotebookScheduleDateKey(t);
  }

  isSlotBooked(slot: NotebookSlot): boolean {
    return this.getSlotOpenCount(slot) <= 0;
  }

  isSlotAlreadyBookedByUser(slot: NotebookSlot): boolean {
    return slot.alreadyBooked === true;
  }

  /**
   * True when the selected date is today and the slot's end time has already passed.
   * A slot ending at "00:00" means midnight (next calendar day) — never past within the same day.
   */
  isSlotInThePast(slot: NotebookSlot): boolean {
    const dateKey = this.notebookForm.get('scheduleDate')?.value as string | undefined;
    if (!dateKey) return false;

    const todayKey = this.formatNotebookScheduleDateKey(new Date());
    if (dateKey !== todayKey) return false;

    const endTime = (slot.endTime ?? '').trim();
    if (!endTime || endTime === '00:00') return false; // midnight end = spans into next day

    const [endH, endM] = endTime.split(':').map(Number);
    if (!Number.isFinite(endH) || !Number.isFinite(endM)) return false;

    const now = new Date();
    const endMinutes = endH * 60 + endM;
    const nowMinutes = now.getHours() * 60 + now.getMinutes();
    return nowMinutes >= endMinutes;
  }

  get pastSlots() {
    return this.availableSlots.filter(s => this.isSlotInThePast(s));
  }

  get upcomingSlots() {
    return this.availableSlots.filter(s => !this.isSlotInThePast(s));
  }

  isSlotEffectivelyDisabled(slot: NotebookSlot): boolean {
    if (this.isSlotAlreadyBookedByUser(slot)) return true;
    if (this.isNotebookScheduleSlotSelected(slot.key)) return false; // always allow deselect
    // Keep upcoming slots enabled even after reaching selection cap.
    return this.isSlotBooked(slot) || this.isSlotInThePast(slot);
  }

  toggleNotebookScheduleSlot(slotKey: string): void {
    const slot = this.availableSlots.find((s) => s.key === slotKey);
    if (!slot) return;
    if (this.isSlotAlreadyBookedByUser(slot) || this.isSlotBooked(slot) || this.isSlotInThePast(slot)) return;

    const ctl = this.notebookForm.get('scheduleSlots');
    const prev = ([] as string[]).concat((ctl?.value as string[]) ?? []);

    if (prev.includes(slotKey)) {
      ctl?.setValue(prev.filter((x) => x !== slotKey));
      ctl?.updateValueAndValidity({ emitEvent: false });
      ctl?.markAsTouched();
      return;
    }

    const next = [...prev, slotKey];
    if (next.length > this.MAX_CONTIGUOUS_SCHEDULE_SLOTS) {
      ctl?.setErrors({ ...(ctl.errors ?? {}), maxContiguousSlots: true });
      ctl?.markAsTouched();
      this.cdr.markForCheck();
      return;
    }
    if (!this.areSelectedScheduleSlotsContiguous(next)) {
      ctl?.setErrors({ ...(ctl.errors ?? {}), nonContiguousSlots: true });
      ctl?.markAsTouched();
      this.cdr.markForCheck();
      return;
    }

    ctl?.setValue(next);
    ctl?.updateValueAndValidity({ emitEvent: false });
    ctl?.markAsTouched();
  }

  isNotebookScheduleSlotSelected(slotKey: string): boolean {
    const ids = this.notebookForm.get('scheduleSlots')?.value as string[];
    return Array.isArray(ids) && ids.includes(slotKey);
  }

  /** Normalises API label: "4h Session" → "4hr Session" */
  formatSlotLabel(label: string): string {
    return label.replace(/\b(\d+)h\b/gi, '$1hr');
  }

  /**
   * Returns an array of booleans for icon rendering.
   * true = available seat, false = taken seat.
   * Capped at 8 icons; if totalSlots > 8 the array still honours the ratio.
   */
  slotSeatIcons(slot: NotebookSlot): boolean[] {
    const total = Math.min(Math.max(slot.totalSlots, 0), 8);
    const available = Math.min(slot.availableSlots, total);
    return Array.from({ length: total }, (_, i) => i < available);
  }

  isGpuNotebookSchedule(): boolean {
    return (this.notebookForm.get('instanceType')?.value ?? '').toString().toUpperCase() === 'GPU';
  }

  getSlotOpenCount(slot: NotebookSlot): number {
    return Math.max(0, Number(slot.availableSlots) || 0);
  }

  getSlotBookedCount(slot: NotebookSlot): number {
    const booked = Number(slot.bookedSlots);
    if (Number.isFinite(booked) && booked >= 0) {
      return booked;
    }
    const total = Number(slot.totalSlots) || 0;
    return Math.max(0, total - this.getSlotOpenCount(slot));
  }

  slotAvailabilityLabel(slot: NotebookSlot): string {
    if (this.isSlotBooked(slot)) return 'Fully booked';
    return `${slot.availableSlots} of ${slot.totalSlots} slots open`;
  }

  private async loadAvailableSlots(dateKey: string): Promise<void> {
    const selectedPlanId = this.notebookForm.get('computePlanId')?.value as string | undefined;
    const selectedPlan = selectedPlanId ? this.computePlanLookup?.[selectedPlanId] : undefined;
    const category = (selectedPlan?.apiCategory ?? selectedPlanId ?? '').toString().trim();
    if (!category || !dateKey) return;

    const baseUrl = this.getSandboxBaseUrlForNotebook(
      this.notebookForm.get('instanceType')?.value ?? 'CPU',
    );

    this.slotsLoading = true;
    this.cdr.markForCheck();

    try {
      const response = await this.sandboxApiService.listAvailableSlots(category, dateKey, baseUrl);
      this.availableSlots = this.extractSlots(response);
    } catch (error) {
      console.error('Error loading available slots:', error);
      this.availableSlots = [];
    } finally {
      this.slotsLoading = false;
      this.notebookForm.get('scheduleSlots')?.updateValueAndValidity({ emitEvent: false });
      this.cdr.markForCheck();
    }
  }

  private extractSlots(response: any): NotebookSlot[] {
    const raw: any[] = Array.isArray(response?.slots)
      ? response.slots
      : Array.isArray(response?.data?.slots)
        ? response.data.slots
        : Array.isArray(response?.result?.slots)
          ? response.result.slots
          : Array.isArray(response)
            ? response
            : [];

    return raw
      .filter((s: any) => s && typeof s.key === 'string')
      .map((s: any): NotebookSlot => {
        const bookedSlots = Number(s.bookedSlots ?? s.booked_slots ?? 0);
        const totalSlots = Number(s.totalSlots ?? s.total_slots ?? 0);
        const rawAvailable = s.availableSlots ?? s.available_slots;
        const parsedAvailable = Number(rawAvailable);
        const availableSlots =
          rawAvailable != null && Number.isFinite(parsedAvailable)
            ? parsedAvailable
            : totalSlots > 0
              ? Math.max(0, totalSlots - bookedSlots)
              : 0;
        return {
          key: s.key,
          label: s.label ?? '',
          startTime: s.startTime ?? '',
          endTime: s.endTime ?? '',
          availability:
            s.availability ?? (availableSlots > 0 ? 'available' : 'booked'),
          availableSlots,
          bookedSlots,
          totalSlots,
          alreadyBooked: Boolean(s.alreadyBooked ?? s.already_booked ?? false),
        };
      });
  }

  getScheduleSlotsFieldError(): string {
    const c = this.notebookForm.get('scheduleSlots');
    if (!c?.errors || !c.touched) return '';
    if (c.errors['slotsRequired']) return 'Select at least one session.';
    if (c.errors['maxContiguousSlots']) {
      return 'You can book up to 2 back-to-back sessions (8 hours total). Select fewer consecutive sessions to continue.';
    }
    if (c.errors['nonContiguousSlots']) {
      return 'Sessions must be booked back-to-back. Select consecutive time slots only.';
    }
    return '';
  }

  getScheduleSelectedDateForPipe(): Date | null {
    const v = this.notebookForm?.get('scheduleDate')?.value;
    if (!v || typeof v !== 'string') return null;
    const [y, m, d] = v.split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  selectUserCategory(value: string): void {
    this.notebookForm.patchValue({ userCategory: value });
    this.notebookForm.get('userCategory')?.markAsTouched();
  }

  private async resolveKeycloakAccessToken(): Promise<string> {
    await this.waitForKeycloakReady();
    const keycloakInstance = this.keycloak.getKeycloakInstance();
    if (!keycloakInstance?.authenticated) {
      throw new Error('User is not authenticated');
    }
    if (keycloakInstance.isTokenExpired()) {
      await keycloakInstance.updateToken(30);
    }
    const token = keycloakInstance.token?.trim();
    if (!token || /^https?:\/\//i.test(token)) {
      throw new Error('Unable to obtain access token');
    }
    return token;
  }

  async onSubmitNotebook(): Promise<void> {
    // Mark all touched to surface inline errors
    Object.keys(this.notebookForm.controls).forEach((key) => {
      this.notebookForm.get(key)?.markAsTouched();
    });

    const formData = this.notebookForm.value;

    const isLocalBrowser = this.isLocalBrowserInstanceType();

    // Manual validation of fields required by create / booking API
    const missingFields: string[] = [];
    if (!isLocalBrowser && !formData.name?.toString().trim()) missingFields.push('notebook name');
    if (!formData.instanceType) missingFields.push('instance type');
    if (!isLocalBrowser) {
      if (!formData.computePlanId) missingFields.push('category');
      if (!formData.scheduleDate) missingFields.push('date');
      if (!Array.isArray(formData.scheduleSlots) || formData.scheduleSlots.length === 0) {
        missingFields.push('session slot');
      }
    }
    if (!formData.policyAgreed) missingFields.push('policy agreement');
    if (isLocalBrowser && !this.isBrowserLaunchAvailable()) {
      missingFields.push('browser launch URL');
    }

    console.log('[CreateNotebook] form values:', formData);
    console.log('[CreateNotebook] form errors:', this.notebookForm.errors);
    Object.keys(this.notebookForm.controls).forEach((k) => {
      const ctrl = this.notebookForm.get(k);
      if (ctrl?.errors) console.log(`  [${k}] errors:`, ctrl.errors, '| value:', ctrl.value);
    });

    if (missingFields.length > 0) {
      console.warn('[CreateNotebook] Blocked — missing:', missingFields);
      this.cdr.markForCheck();
      return;
    }

    const scheduleSlotsControl = this.notebookForm.get('scheduleSlots');
    if (!isLocalBrowser && scheduleSlotsControl?.invalid) {
      scheduleSlotsControl.markAsTouched();
      this.cdr.markForCheck();
      return;
    }

    if (true) {
      console.log('Creating notebook:', formData);

      try {
        this.loading = true;
        this.clearMyProjectFlowAlert();

        if (isLocalBrowser) {
          const notebookName =
            (formData.name as string)?.trim() ||
            this.browserCategory?.displayName?.trim() ||
            'jupyter-lite';
          let accessToken: string;
          try {
            accessToken = await this.resolveKeycloakAccessToken();
          } catch {
            this.showMyProjectFlowError(
              'Please sign in again to launch the browser notebook.',
            );
            return;
          }

          const baseUrl = this.getSandboxBaseUrlForNotebook('CPU');
          const session = await this.sandboxApiService.createJupyterLiteSession({
            accessToken,
            baseUrl,
          });
          const launchUrl = this.buildSandboxBrowserLaunchUrl(session.launchUrl);
          if (!launchUrl) {
            this.showMyProjectFlowError(
              'Browser notebook is not available right now. Please try again later.',
            );
            return;
          }

          this.googleAnalytics.trackMyProjectNotebookCreate(notebookName);
          window.open(launchUrl, '_blank', 'noopener,noreferrer');
          this.toasterService.success('Opening notebook in your browser');

          this.clearNotebookCreateDraft();
          this.notebookForm.reset();
          this.initNotebookForm();
          this.currentStep = 1;
          return;
        }

        const selectedPlanId = (formData.computePlanId as string) || '';
        const fallbackPlan =
          this.getComputePlansForCurrentInstanceType()[0] || this.computePlanLookup[this.defaultComputePlanId];
        const planId = selectedPlanId || fallbackPlan?.id || this.defaultComputePlanId;
        const plan = this.computePlanLookup?.[planId];

        const scheduleDate = formData.scheduleDate as string;
        const scheduleSlots = (formData.scheduleSlots as string[]) ?? [];
        const category = plan?.apiCategory ?? planId.replace(/-/g, '_');

        const bookingPayload = {
          category,
          notebookName: formData.name as string,
          slotDate: scheduleDate,
          slotKeys: scheduleSlots,
        };

        console.log('Creating booking with payload:', bookingPayload);
        const baseUrl = this.getSandboxBaseUrlForNotebook(plan?.apiType ?? formData.instanceType);
        const response = await this.sandboxApiService.createBooking(bookingPayload, baseUrl);
        console.log('Notebook created successfully:', response);
        this.googleAnalytics.trackMyProjectNotebookCreate(formData.name);

        this.toasterService.success("You've successfully created a Notebook");
        this.clearMyProjectFlowAlert();

        // Reset form and switch to list tab
        this.clearNotebookCreateDraft();
        this.notebookForm.reset();
        this.initNotebookForm();
        this.currentStep = 1;
        this.resetNotebookListFilters(false);
        this.activeTab = 'list';
        this.updateTabQueryParam('list');

        // Refresh the notebook list after successful creation
        this.notebooksLoaded = false; // Reset flag to allow reload
        await this.loadNotebooks();
      } catch (error: unknown) {
        console.error('Error creating notebook:', error);
        const detail = this.extractApiErrorDetail(error);
        // Credits UI is disabled in MahaAgX — do not show credit-limit errors in the UI.
        if (!this.isCreditLimitExceededError(detail)) {
          this.showMyProjectFlowError(this.formatNotebookCreateErrorMessage(detail));
        }
        // Keep form open so user can retry
      } finally {
        this.loading = false;
      }
    }
  }

  openPolicyModal(): void {
    this.showPolicyModal = true;
  }

  closePolicyModal(): void {
    this.showPolicyModal = false;
  }

  /**
   * Open notebook - get URL and show confirmation modal (spotlight UI)
   */
  async openNotebook(row: ProjectData, demoDisplayTitle?: string): Promise<void> {
    const isDemoCard =
      typeof demoDisplayTitle === 'string' && demoDisplayTitle.trim().length > 0;
    const displayName =
      demoDisplayTitle?.trim() ||
      row.notebookName ||
      row.id ||
      'Notebook';

    if (isDemoCard) {
      this.showNotebookReadyConfirmation(row, null, {
        heading: 'Notebook is ready',
        subline: `${displayName} is available. Open it in a new browser tab to continue working.`,
        cta: 'Open notebook',
      });
      return;
    }

    try {
      if (row.notebookUrl) {
        await this.openNotebookUrlWithTokenSession(row, row.notebookUrl);
        return;
      }

      const notebookName = row.notebookName || row.id;
      if (!notebookName) {
        this.showMyProjectFlowError('Notebook name is missing. Cannot open notebook.');
        return;
      }

      // Get notebook status/URL from API (pass base URL by notebook type for CPU/GPU)
      const baseUrl = this.getSandboxBaseUrlForNotebook(row.type);
      const response = await this.sandboxApiService.notebookStatusCheck(notebookName, baseUrl);
      console.log('Notebook status check response for opening:', response);
      
      // Extract notebook URL from response
      let notebookUrl: string | null = null;
      if (response?.url) {
        notebookUrl = response.url;
      } else if (response?.data?.url) {
        notebookUrl = response.data.url;
      } else if (response?.result?.url) {
        notebookUrl = response.result.url;
      } else if (response?.notebookUrl) {
        notebookUrl = response.notebookUrl;
      } else if (response?.data?.notebookUrl) {
        notebookUrl = response.data.notebookUrl;
      } else if (response?.result?.notebookUrl) {
        notebookUrl = response.result.notebookUrl;
      }
      
      // Show the confirmation modal with the notebook URL
      this.showNotebookReadyConfirmation(row, notebookUrl);
    } catch (error) {
      console.error('Error getting notebook URL:', error);
      // Still show modal with constructed URL as fallback
      this.showNotebookReadyConfirmation(row, null);
    }
  }

  /**
   * Get sandbox base URL for a notebook by type (CPU vs GPU).
   * Uses environment.baseSandboxPathUrlCpu / baseSandboxPathUrlGpu; falls back to baseSandboxPathUrl (IUDX).
   */
  getSandboxBaseUrlForNotebook(type?: string): string {
    const t = (type || '').trim().toUpperCase();
    const env = environment as {
      baseSandboxPathUrlCpu?: string;
      baseSandboxPathUrlGpu?: string;
      baseSandboxPathUrl?: string;
    };
    const legacyBase = (env.baseSandboxPathUrl ?? '').trim();
    const cpuBase = (env.baseSandboxPathUrlCpu ?? legacyBase).trim();
    const gpuBase = (env.baseSandboxPathUrlGpu ?? cpuBase).trim();
    if (t === 'GPU') {
      return gpuBase || cpuBase;
    }
    if (t === 'CPU') return cpuBase;
    return cpuBase;
  }

  /**
   * Show confirmation modal when notebook status changes to "Running"
   */
  showNotebookReadyConfirmation(
    notebook: ProjectData,
    notebookUrl?: string | null,
    copy?: { heading?: string; subline?: string; cta?: string },
  ): void {
    const name = notebook.notebookName || notebook.id || '';
    this.notebookReadyModalHeading =
      copy?.heading ?? 'Your notebook is ready';
    this.notebookReadyModalSubline =
      copy?.subline ??
      `${name ? name + ' is' : 'Your notebook is'} running. Open it in a new browser tab when you're ready.`;
    this.notebookReadyModalCta = copy?.cta ?? 'Open notebook';

    this.readyNotebook = notebook;
    
    // Use URL from API response if available, otherwise construct it
    if (notebookUrl) {
      this.readyNotebookUrl = notebookUrl;
    } else {
      // Construct notebook URL from notebook name; use CPU/GPU base URL by type
      const notebookName = notebook.notebookName || notebook.id;
      const baseUrl = this.getSandboxBaseUrlForNotebook(notebook.type);
      this.readyNotebookUrl = `${baseUrl}notebook/${notebookName}`;
    }
    
    this.showNotebookReadyModal = true;
  }

  /**
   * Handle proceed button click - open notebook in new tab
   */
  onProceedToNotebook(): void {
    if (this.readyNotebookUrl && this.readyNotebook) {
      void this.openNotebookUrlWithTokenSession(this.readyNotebook, this.readyNotebookUrl).finally(
        () => this.closeNotebookReadyModal(),
      );
      return;
    }
    this.closeNotebookReadyModal();
  }

  /** Booking id for sandbox notebook open (`/v1/bookings/{id}/notebook-token-session`). */
  private resolveProjectBookingId(row: ProjectData): string {
    const booking = (row as ProjectData & { _booking?: { id?: unknown; bookingId?: unknown } })
      ._booking;
    return String(row.id ?? booking?.id ?? booking?.bookingId ?? '').trim();
  }

  private isCpuOrGpuNotebook(row: ProjectData): boolean {
    const type = (row.type || '').trim().toUpperCase();
    return type === 'CPU' || type === 'GPU';
  }

  /**
   * For CPU/GPU notebooks, POST notebook-token-session before opening the lab URL.
   * Opens on HTTP 200; otherwise shows the API error in a toaster.
   */
  private async openNotebookUrlWithTokenSession(row: ProjectData, url: string): Promise<void> {
    const targetUrl = (url || '').trim();
    if (!targetUrl) {
      this.toasterService.error('Notebook URL is missing. Cannot open notebook.');
      return;
    }

    if (!this.isCpuOrGpuNotebook(row)) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const bookingId = this.resolveProjectBookingId(row);
    if (!bookingId) {
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    try {
      const baseUrl = this.getSandboxBaseUrlForNotebook(row.type);
      await this.sandboxApiService.createNotebookTokenSession(bookingId, baseUrl);
      window.open(targetUrl, '_blank', 'noopener,noreferrer');
    } catch (error) {
      console.error('Error creating notebook token session:', error);
      const detail = this.extractApiErrorDetail(error);
      this.toasterService.error(
        detail || 'Unable to open notebook. Please try again.',
      );
    }
  }

  /**
   * Check if user has Compute role
   */
  private async checkComputeRole(): Promise<void> {
    try {
      console.log('Checking Compute role for GPU access...');
      const userData = await this.authApiService.getUserInfo();
      console.log('User API response for role check:', userData);
      
      // Extract result if nested, otherwise use response directly
      const actualUserData = userData?.['result'] || userData;
      const userRoles = actualUserData?.roles || [];
      
      // Extract pendingRoles - check multiple possible locations
      this.pendingRoles = actualUserData?.['pendingRoles'] || 
                         userData?.['pendingRoles'] || 
                         [];
      
      console.log('Full user data:', actualUserData);
      console.log('Pending roles:', this.pendingRoles);
      console.log('Has pending compute role:', this.hasPendingComputeRole());
      
      // Check if user has compute role (in active roles array)
      const hasActiveComputeRole = userRoles.some((role: string) => role.toLowerCase() === 'compute');
      this.hasActiveComputeRole = hasActiveComputeRole;
      
      // Check if user has compute role in pending roles
      const hasPendingCompute = this.hasPendingComputeRole();
      
      // User has compute role if it's either active or pending (for enabling GPU / Request Credits)
      this.hasComputeRole = hasActiveComputeRole || hasPendingCompute;
      
      console.log('User roles:', userRoles, 'Has active Compute role:', hasActiveComputeRole, 'Has pending Compute role:', hasPendingCompute, 'Has Compute role (any):', this.hasComputeRole);
      
      // Trigger change detection to update the warning message
      this.cdr.detectChanges();
      
      // If user doesn't have Compute in active roles and GPU is selected, switch to CPU
      if (!this.hasActiveComputeRole && this.notebookForm.get('instanceType')?.value === 'GPU') {
        this.notebookForm.patchValue({ instanceType: 'CPU', computePlanId: this.defaultComputePlanId });
        this.cdr.detectChanges();
      }
    } catch (error) {
      console.error('Error checking Compute role:', error);
      // On error, assume no compute role for safety
      this.hasComputeRole = false;
      this.hasActiveComputeRole = false;
      this.pendingRoles = [];
    }
  }
  
  /**
   * Check if GPU option is disabled.
   * Disabled when user lacks active Compute role.
   */
  isGpuDisabled(): boolean {
    return !this.hasActiveComputeRole;
  }
  
  /**
   * Check if compute role is pending approval
   */
  hasPendingComputeRole(): boolean {
    return this.pendingRoles.some((role: string) => role.toLowerCase() === 'compute');
  }
  

  /**
   * Handle stop button click - stop the notebook
   */
  async onStopNotebookFromModal(): Promise<void> {
    console.log('onStopNotebookFromModal called');
    console.log('readyNotebook:', this.readyNotebook);
    
    if (!this.readyNotebook) {
      console.warn('No ready notebook found to stop');
      return;
    }

    const notebookName = this.readyNotebook.notebookName;
    console.log('Notebook name to stop:', notebookName);
    
    if (!notebookName) {
      console.warn('Notebook name is missing');
      this.showMyProjectFlowError('Notebook name is missing. Cannot stop notebook.');
      this.closeNotebookReadyModal();
      return;
    }

    // Stop the notebook without showing loading skeleton
    try {
      const payload = {
        name: notebookName
      };
      
      console.log('=== STOPPING NOTEBOOK FROM MODAL ===');
      console.log('Payload:', payload);
      console.log('sandboxApiService:', this.sandboxApiService);
      console.log('Calling sandboxApiService.stopNotebook...');
      const baseUrl = this.getSandboxBaseUrlForNotebook(this.readyNotebook?.type);
      const response = await this.sandboxApiService.stopNotebook(payload, baseUrl);
      console.log('=== NOTEBOOK STOPPED SUCCESSFULLY ===');
      console.log('Response:', response);
      
      // Close modal after successful API call
      this.closeNotebookReadyModal();
      
      // Update the status locally immediately for better UX
      const notebookIndex = this.projectsData.findIndex(
        (n) => n.notebookName === notebookName
      );
      if (notebookIndex !== -1) {
        this.projectsData[notebookIndex].status = 'Stopped';
        this.rebuildNotebookListCards();
        this.cdr.detectChanges();
      }

      // Refresh the notebook list after a delay without showing skeleton
      setTimeout(async () => {
        this.notebooksLoaded = false;
        await this.loadNotebooks(true); // Pass true to skip loading state
      }, 1000);
    } catch (error) {
      console.error('Error stopping notebook from modal:', error);
      this.showMyProjectFlowError('Failed to stop notebook. Please try again.');
      // Close modal even on error
      this.closeNotebookReadyModal();
      // Refresh list on error - but try to avoid skeleton
      setTimeout(async () => {
        this.notebooksLoaded = false;
        await this.loadNotebooks(true); // Pass true to skip loading state
      }, 500);
    }
  }

  /**
   * Close the notebook ready modal
   */
  closeNotebookReadyModal(): void {
    this.showNotebookReadyModal = false;
    this.readyNotebook = null;
    this.readyNotebookUrl = null;
    this.notebookReadyModalHeading = '';
    this.notebookReadyModalSubline = '';
    this.notebookReadyModalCta = 'Open notebook';
  }

  /**
   * Check if there are any notebooks with "Opening" status
   */
  hasOpeningNotebooks(): boolean {
    return this.projectsData.some(notebook => this.isOpeningStatus(notebook.status));
  }

  /**
   * Get all notebooks with "Opening" status
   */
  getOpeningNotebooks(): ProjectData[] {
    return this.projectsData.filter(notebook => this.isOpeningStatus(notebook.status));
  }

  /**
   * HostListener for browser navigation (beforeunload)
   */
  @HostListener('window:beforeunload', ['$event'])
  onBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.hasOpeningNotebooks()) {
      event.preventDefault();
      event.returnValue = 'You have notebooks that are opening. Please wait or delete them before leaving.';
      return event.returnValue;
    }
  }

  /**
   * Intercept navigation attempts
   * This should be called before any navigation
   */
  async checkNavigationBeforeLeave(url?: string): Promise<boolean> {
    if (this.hasOpeningNotebooks()) {
      this.openingNotebooks = this.getOpeningNotebooks();
      this.pendingNavigation = url || null;
      this.showNavigationGuardModal = true;
      this.cdr.detectChanges();
      return false; // Prevent navigation
    }
    return true; // Allow navigation
  }

  /**
   * Handle user choice: Wait for notebooks to open
   */
  async handleWaitForNotebooks(): Promise<void> {
    this.showNavigationGuardModal = false;
    this.pendingNavigation = null;
    this.openingNotebooks = [];
    this.cdr.detectChanges();
    // User stays on the page, notebooks continue opening
  }

  /**
   * Handle user choice: Delete opening notebooks
   */
  async handleDeleteOpeningNotebooks(): Promise<void> {
    try {
      const notebooksToDelete = [...this.openingNotebooks];
      const pendingNav = this.pendingNavigation;
      
      // Delete all opening notebooks
      for (const notebook of notebooksToDelete) {
        try {
          await this.deleteNotebook(notebook);
        } catch (error) {
          console.error(`Error deleting notebook ${notebook.notebookName}:`, error);
        }
      }
      
      this.showNavigationGuardModal = false;
      this.openingNotebooks = [];
      
      // If there was a pending navigation, proceed with it
      if (pendingNav) {
        this.pendingNavigation = null;
        // Wait a bit for the deletion to complete and UI to update
        setTimeout(() => {
          if (pendingNav.startsWith('/')) {
            this.router.navigateByUrl(pendingNav);
          } else {
            // Handle relative paths
            this.router.navigate([pendingNav]);
          }
        }, 300);
      }
      
      this.cdr.detectChanges();
    } catch (error) {
      console.error('Error deleting opening notebooks:', error);
      this.showMyProjectFlowError('Failed to delete some notebooks. Please try again.');
    }
  }

  /**
   * Close navigation guard modal without action
   */
  closeNavigationGuardModal(): void {
    this.showNavigationGuardModal = false;
    this.pendingNavigation = null;
    this.openingNotebooks = [];
    this.cdr.detectChanges();
  }

  /**
   * Open upgrade role modal by dispatching custom event
   */
  openUpgradeRoleModal(): void {
    const event = new CustomEvent('openUpgradeModal', {
      detail: { source: 'my-projects' }
    });
    window.dispatchEvent(event);
  }

  /**
   * CanDeactivate implementation for route guard
   */
  canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
    if (!this.hasOpeningNotebooks()) {
      return true;
    }

    // Show the modal and return a promise that resolves based on user action
    return new Promise<boolean>((resolve) => {
      this.openingNotebooks = this.getOpeningNotebooks();
      this.showNavigationGuardModal = true;
      this.cdr.detectChanges();

      // Store the resolve function so we can call it when user makes a choice
      (this as any)._navigationResolve = resolve;
    });
  }

  /**
   * Handle user choice: Wait for notebooks to open (cancel navigation)
   */
  async handleWaitForNotebooksNavigation(): Promise<void> {
    const resolve = (this as any)._navigationResolve;
    if (resolve) {
      (this as any)._navigationResolve = null;
      resolve(false); // Cancel navigation
    }
    await this.handleWaitForNotebooks();
  }

  /**
   * Handle user choice: Delete opening notebooks (allow navigation)
   */
  async handleDeleteOpeningNotebooksNavigation(): Promise<void> {
    const resolve = (this as any)._navigationResolve;
    if (resolve) {
      (this as any)._navigationResolve = null;
    }
    
    try {
      const notebooksToDelete = [...this.openingNotebooks];
      
      // Delete all opening notebooks
      for (const notebook of notebooksToDelete) {
        try {
          await this.deleteNotebook(notebook);
        } catch (error) {
          console.error(`Error deleting notebook ${notebook.notebookName}:`, error);
        }
      }
      
      this.showNavigationGuardModal = false;
      this.openingNotebooks = [];
      this.cdr.detectChanges();
      
      // Resolve to allow navigation
      if (resolve) {
        resolve(true);
      }
    } catch (error) {
      console.error('Error deleting opening notebooks:', error);
      this.showMyProjectFlowError('Failed to delete some notebooks. Please try again.');
      // Still allow navigation if user wants to proceed
      if (resolve) {
        resolve(true);
      }
    }
  }
}
